// Packages
import React, {
    useState, useEffect, useLayoutEffect, useMemo, useRef, startTransition,
} from 'react';
import { formatLocalizedDate, formatDateForRequest } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/dateFormatService';
import throttle from 'lodash/throttle';
import { toast } from 'react-toastify';
import {
    format,
    parseISO,
    parse,
    getMonth,
    getYear,
    isValid,
} from 'date-fns';
import loadable from '@loadable/component';
import Skeleton from 'react-loading-skeleton';
// Services
import { HORIZONTAL_ORIENTATION } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/constants';
import { reportError } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/reportService';
import { retryLoadableImport } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/retryService';
import FacilityUnitService from '@PROJECT_ROOT/assets/responsive-frontend/js/apiServices/FacilityUnitService';
import windowManipulationService from '@PROJECT_ROOT/assets/responsive-frontend/js/services/windowManipulationService';
import facilityWindowManipulationService from '@PROJECT_ROOT/assets/responsive-frontend/js/services/facilityWindowManipulationService';
import disableElementService from '@PROJECT_ROOT/assets/responsive-frontend/js/services/disableElementService';
import injectService from '@PROJECT_ROOT/assets/responsive-frontend/js/services/injectService';
import { IS_FRAME_SERVICE, keepStickyVisibleForSelectorOnDesktop, setScrollTop } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/frameService';
import { getAdriaticLang } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/apiService';
import { checkIsUnitWishlistedByUser } from '@PROJECT_ROOT/assets/responsive-frontend/js/apiServices/WishlistService';
import {
    dispatchTrackingEvent,
    CLOSEST_BEACH,
    OTHER_BEACHES,
    RESERVATION_FORM_BOOK_NOW_CTA_CLICK,
    RESERVATION_FORM_DATES_ON_CALENDAR_SELECTED,
    RESERVATION_FORM_DETAILED_PRICE_CALCULATION,
    RESERVATION_FORM_GLOBAL_DATES_SELECTED,
    RESERVATION_FORM_GLOBAL_PERSONS_SELECTED,
    RESERVATION_FORM_MOBILE_DOCK_OPEN,
    RESERVATION_FORM_PERSONS_EDIT,
    RESERVATION_FORM_REMOVE_MAIN_UNIT,
    RESERVATION_FORM_REMOVE_UNIT,
    UNIT_PRESENTATION_AMENITY_DETAILS_OPEN,
    UNIT_PRESENTATION_LAST_MINUTE_INTERVAL_SELECT,
    UNIT_PRESENTATION_MAIN_BEACH_GALLERY_NEXT,
    UNIT_PRESENTATION_MAIN_GALLERY_OPEN,
    UNIT_PRESENTATION_MAP_LOCATION_FROM_LOCATION_OPEN,
    UNIT_PRESENTATION_MAP_LOCATION_FROM_OVERVIEW_OPEN,
    UNIT_PRESENTATION_OTHER_BEACHES_GALLERY_NEXT,
    UNIT_PRESENTATION_OTHER_BEACHES_OPEN,
    UNIT_PRESENTATION_PRICELIST_CAPACITY_SELECTED,
    UNIT_PRESENTATION_QUICK_MENU_SELECT_SECTION,
    UNIT_PRESENTATION_REVIEWS_FROM_GRADE_OPEN,
    UNIT_PRESENTATION_REVIEWS_FROM_SHOW_MORE_OPEN,
    UNIT_PRESENTATION_ROOM_DETAILS_OPEN,
    UNIT_PRESENTATION_SIGN_IN_CLICK,
    UNIT_PRESENTATION_DESCRIPTION_SHOW_MORE,
} from '@PROJECT_ROOT/assets/responsive-frontend/js/services/tracking';
// Utils
import {
    updateSearchFormDataFromSelectedUnits,
    sumAllUnitGuests,
    sumAllUnitChildren,
    formatGuestsDisplayText,
    sumAllReservationChildren,
} from '@PROJECT_ROOT/assets/responsive-frontend/js/services/utils';
// Hooks
import useModalState from '@PROJECT_ROOT/assets/responsive-frontend/js/custom-hooks/useModalState';
import useIsMounted from '@PROJECT_ROOT/assets/responsive-frontend/js/custom-hooks/useIsMounted';
// Constants
import {
    DETAILS_MODAL_ID,
    FACILITY_MAP_MODAL_ID,
    FACILITY_UNIT_DESCRIPTION_MODAL_ID,
    GALLERY_MODAL_ID,
    GUEST_SELECT_MODAL_ID,
    PAYMENT_DETAILS_MODAL_ID,
    RESERVATION_MOBILE_MODAL_ID,
    REVIEWS_MODAL_ID, UNIT_MODAL_ID,
} from '@PROJECT_ROOT/assets/responsive-frontend/js/constants/modalIds';
import { BREAKPOINT_MD_PX } from '@PROJECT_ROOT/assets/responsive-frontend/js/constants/breakpoints';
// Components
import FacilitySkeleton from '@PROJECT_ROOT/assets/responsive-frontend/js/components/facilitySkeleton';
import ModalControl, { ModalBody } from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/modalControl';
// Parts
import BreadCrumbs from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/breadcrumbs';
import useEcommerceTracking from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/facilityUnitEcommerceTracking';
import SocialShare from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/socialShare';
import AxiosError from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/axiosError';
import FacilityUnitCard from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/FacilityUnitCard';
import UnitRoomBox from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/UnitRoomBox';
import userData from '@PROJECT_ROOT/assets/responsive-frontend/js/services/userData';
import DateRangePicker from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/dateRangePicker';
import SingleReview from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/SingleReview';
import LoginForMoreBenefits from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/LoginForMoreBenefits';
import WishlistWidget from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/WishlistWidget';
import FacilityUnitFAQ from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/FacilityUnitFAQ';
// Config
import pageConfig from '@PROJECT_ROOT/responsiveFrontendPagesConfiguration.json';
// Icons
import {
    MdArrowRight,
    MdArrowLeft,
    MdLocationOn,
    MdOutlineStar,
    MdArrowDropDown,
    MdArrowDropUp,
    MdCheck,
    MdClose,
    MdInsertLink,
    MdOutlineQueryBuilder,
    MdFilter,
    MdOutlineShare,
    MdOutlineInfo,
    MdInfoOutline,
    MdOutlinePersonAddAlt,
    MdOutlineCreate,
    MdOutlineGridView,
    MdOutlineStraighten,
    MdChevronRight,
    MdOutlineNaturePeople,
    MdOpenInNew,
    MdOutlineReceiptLong,
} from 'react-icons/md';
import { HiOutlineCalendar } from 'react-icons/hi';
import runAfterNextPaint from '@PROJECT_ROOT/assets/responsive-frontend/js/services/runAfterNextPaint';
// Loadable importi - komponente koje se ne prikazuju inicijalno (npr. modali) ili komponente koje nisu u inicijalnom viewportu i nisu bitne za SEO.
// TODO: Prebacit u service ako se pokaze korisno.
const Carousel = loadable(
    () => retryLoadableImport(() => import(
        /* webpackChunkName: "responsiveFrontendCarouselFromFacilityUnitContent" */'@PROJECT_ROOT/assets/responsive-frontend/js/parts/carousel'
    ))
        .catch((err) => {
            err.customMessage = 'RETRY IMPORT-a - error sa importa Carousel u FacilityUnitContent komponenti.';
            reportError(err);
            return { default: () => '' };
        }),
);
const ReservationMobileModal = loadable(
    () => retryLoadableImport(() => import(
        /* webpackChunkName: "responsiveFrontendReservationMobileModalFromFacilityUnitContent" */'@PROJECT_ROOT/assets/responsive-frontend/js/parts/ReservationMobileModal'
    ))
        .catch((err) => {
            err.customMessage = 'RETRY IMPORT-a - error sa importa ReservationMobileModal u FacilityUnitContent komponenti.';
            reportError(err);
            return { default: () => '' };
        }),
);
const GuestSelectModal = loadable(
    () => retryLoadableImport(() => import(
        /* webpackChunkName: "responsiveFrontendGuestSelectModalFromFacilityUnitContent" */'@PROJECT_ROOT/assets/responsive-frontend/js/parts/GuestSelectModal'
    ))
        .catch((err) => {
            err.customMessage = 'RETRY IMPORT-a - error sa importa GuestSelectModal u FacilityUnitContent komponenti.';
            reportError(err);
            return { default: () => '' };
        }),
);
const FacilityGallery = loadable(
    () => retryLoadableImport(() => import(
        /* webpackChunkName: "responsiveFrontendFacilityGalleryFromFacilityUnitContent" */'@PROJECT_ROOT/assets/responsive-frontend/js/parts/facilityGallery'
    ))
        .catch((err) => {
            err.customMessage = 'RETRY IMPORT-a - error sa importa FacilityGallery u FacilityUnitContent komponenti.';
            reportError(err);
            return { default: () => '' };
        }),
);
const FacilityReviews = loadable(
    () => retryLoadableImport(() => import(
        /* webpackChunkName: "responsiveFrontendFacilityReviewsFromFacilityUnitContent" */'@PROJECT_ROOT/assets/responsive-frontend/js/parts/facilityReviews'
    ))
        .catch((err) => {
            err.customMessage = 'RETRY IMPORT-a - error sa importa FacilityReviews u FacilityUnitContent komponenti.';
            reportError(err);
            return { default: () => '' };
        }),
);
const FacilityDetails = loadable(
    () => retryLoadableImport(() => import(
        /* webpackChunkName: "responsiveFrontendFacilityDetailsFromFacilityUnitContent" */'@PROJECT_ROOT/assets/responsive-frontend/js/parts/facilityDetails'
    ))
        .catch((err) => {
            err.customMessage = 'RETRY IMPORT-a - error sa importa FacilityDetails u FacilityUnitContent komponenti.';
            reportError(err);
            return { default: () => '' };
        }),
);
const FacilityMap = loadable(
    () => retryLoadableImport(() => import(
        /* webpackChunkName: "responsiveFrontendFacilityMapFromFacilityUnitContent" */'@PROJECT_ROOT/assets/responsive-frontend/js/parts/facilityMap'
    ))
        .catch((err) => {
            err.customMessage = 'RETRY IMPORT-a - error sa importa FacilityMap u FacilityUnitContent komponenti.';
            reportError(err);
            return { default: () => '' };
        }),
);
const PaymentDetails = loadable(
    () => retryLoadableImport(() => import(
        /* webpackChunkName: "responsiveFrontendPaymentDetailsFromFacilityUnitContent" */'@PROJECT_ROOT/assets/responsive-frontend/js/parts/paymentDetailsModal'
    ))
        .catch((err) => {
            err.customMessage = 'RETRY IMPORT-a - error sa importa PaymentDetails u FacilityUnitContent komponenti.';
            reportError(err);
            return { default: () => '' };
        }),
);
const UnitDetails = loadable(
    () => retryLoadableImport(() => import(
        /* webpackChunkName: "responsiveFrontendUnitDetailsFromFacilityUnitContent" */'@PROJECT_ROOT/assets/responsive-frontend/js/parts/unitDetails'
    ))
        .catch((err) => {
            err.customMessage = 'RETRY IMPORT-a - error sa importa UnitDetails u FacilityUnitContent komponenti.';
            reportError(err);
            return { default: () => '' };
        }),
);

// TODO: #58308 Privremeno, nakon comitt-a prve verzije prebacit u servis/helper.
// Služi za pretvaranje state-a u query string.
function toQueryString(obj, prefix) {
    return Object.entries(obj)
        // eslint-disable-next-line
        .filter(([_, value]) => value !== undefined && value !== null && value !== '')
        .reduce((acc, [key, value]) => {
            if (Array.isArray(value)) {
                const arr = value
                    .map((item, i) => toQueryString(item, `${prefix ? `${prefix}[${key}]` : key}[${i}]`))
                    .filter(Boolean)
                    .join('&');
                if (arr) acc.push(arr);
            } else if (typeof value === 'object' && value !== null) {
                const nested = toQueryString(value, prefix ? `${prefix}[${key}]` : key);
                if (nested) acc.push(nested);
            } else {
                const encodedKey = prefix ? `${prefix}[${key}]` : key;
                acc.push(`${encodeURIComponent(encodedKey)}=${encodeURIComponent(value)}`);
            }
            return acc;
        }, [])
        .join('&');
}

function getSelectedUnitsFromApi(apiUnits) {
    return apiUnits
        .filter((unit) => unit.selected)
        .map((unit) => ({
            unitId: unit.unitId,
            adultsCount: unit.adultsCount,
            children0to3Count: unit.children0to3Count,
            children4to11Count: unit.children4to11Count,
            children12to17Count: unit.children12to17Count,
        }));
}

function getMonthAndYearFromISODate(isoDateStr) {
    const date = parseISO(isoDateStr);
    if (!isValid(date)) {
        return null;
    }
    return {
        month: getMonth(date) + 1, // 1–12 zbog logike date-fns.
        year: getYear(date),
    };
}

const equalMonthAndYearIdentifier = 'equalMonthAndYearIdentifier';
function compareMonthYearObjects(a, b) {
    if (!a || !b || typeof a.year !== 'number' || typeof a.month !== 'number' || typeof b.year !== 'number' || typeof b.month !== 'number') {
        // Ostavljam ako mi zatreba prepoznavanje.
        return 'invalidMonthAndYearIdentifier';
    }
    if (a.year < b.year || (a.year === b.year && a.month < b.month)) {
        // Ostavljam ako mi zatreba prepoznavanje.
        return 'lessMonthAndYearIdentifier';
    }
    if (a.year > b.year || (a.year === b.year && a.month > b.month)) {
        // Ostavljam ako mi zatreba prepoznavanje.
        return 'greaterMonthAndYearIdentifier';
    }
    return equalMonthAndYearIdentifier;
}

// Provjerava je li mjesec prvi ili zadnji u učitanim podacima kalendara (tada treba dohvatiti susjedne mjesece).
function isMonthOnEdgeOfLoadedCalendar(monthAndYear, firstLoadedMonthAndYear, lastLoadedMonthAndYear) {
    return compareMonthYearObjects(monthAndYear, lastLoadedMonthAndYear) === equalMonthAndYearIdentifier
        || compareMonthYearObjects(monthAndYear, firstLoadedMonthAndYear) === equalMonthAndYearIdentifier;
}

// TODO: #58308 Privremeno, nakon comitt-a prve verzije će se uklonit ili doradit.
// NAKNADNO UKLJUČIT IGNORIRANJE, vidit sa backendom sta ne triba.
// const reservationParametersToIgnoreIfUnchanged = {
//     intervalFrom: '',
//     intervalTo: '',
//     recommendingPartnerOrPromoFlyer: '',
//     searchFormChildren12to17Count: 0,
//     searchFormChildren4to11Count: 0,
//     searchFormChildren0to3Count: 0,
//     isFrameService: 0,
//     isHtmlService: 0,
//     isMobileVersion: 0,
// };

const initialReservationParameters = {
    facilityId: null,
    intervalFrom: '',
    intervalTo: '',
    recommendingPartnerOrPromoFlyer: '',
    isFrameService: 0,
    isHtmlService: 0,
    isMobileVersion: 0,
    selectedUnits: [],
    searchFormAdultsCount: 0,
    searchFormChildren12to17Count: 0,
    searchFormChildren4to11Count: 0,
    searchFormChildren0to3Count: 0,
};

// TODO: #58308 Odradit/doradit će se nakon prve verzije:
// - Zaraz logika po staroj verziji site-a.
// - Uklonit nepotrebni kod (importe, state-ove, constant-e, logiku).
// - Uklonit disable-anje lintera di se to može.
function FacilityUnitContent({
    temporaryPublicResourcesSSR,
    presentationUnitSSR,
    temporaryParametersDataSSR,
    serverSideRenderParameters,
}) {
    const isMounted = useIsMounted();
    // Constant-e (izbacit ih van komponente).
    const ssrParameters = temporaryParametersDataSSR || serverSideRenderParameters || null;
    const isClientSideRendering = windowManipulationService.isBrowserEnvironment();
    function getMainID(ssrParametersID, getClientValue) {
        if (ssrParametersID != null) return Number(ssrParametersID);
        if (isClientSideRendering) return Number(getClientValue());
        return null;
    }
    const twoLetterISOLanguageCodeParameter = getAdriaticLang();
    const currentUser = ssrParameters?.userJWT && ssrParameters.userJWT !== '0'
        ? userData.decodeToken(ssrParameters.userJWT)
        : userData.getDecodedUserDataFromLocalStorage();
    const mainUnitID = getMainID(ssrParameters?.unitID, facilityWindowManipulationService.getUnitID);
    const mainFacilityID = getMainID(ssrParameters?.facilityID, facilityWindowManipulationService.getFacilityID);
    const axiosCustomErrors = isClientSideRendering && JSON.parse(document.getElementById('mainWrapper')?.getAttribute('data-axios')); // Pribacit u servis ili poslat reportService-u flag da baci toast poruku.
    const maxLengthForInitialRobinsonDescription = 200;
    // Služi za pogađanje točnog scrolla na vrh elementa (160px je visina header i navigacija);
    const heightToScrollIntoView = 80;
    const mobileBreakPoint = 767.98;
    const shareURL = isClientSideRendering && window.location.href;
    // TODO: false zaminut sa isSmallDevice logikom.
    const orientation = HORIZONTAL_ORIENTATION;
    // STATE-ovi
    // Fetch rezultati.
    const [presentationUnit, setPresentationUnit] = useState(temporaryPublicResourcesSSR || presentationUnitSSR || null);
    const [unitPriceCalculation, setUnitPriceCalculation] = useState(null);
    const [missingUnitIdsInUnitPriceCalculation, setMissingUnitIdsInUnitPriceCalculation] = useState([]);
    const [unitPriceAvailabilityCalendar, setUnitPriceAvailabilityCalendar] = useState(null);
    const [facilityUnitReviews, setFacilityUnitReviews] = useState(null);
    const [presentationUnitAmenities, setPresentationUnitAmenities] = useState(null);
    const [unitMap, setUnitMap] = useState(null);
    const [unitMainGallery, setUnitMainGallery] = useState(null);
    const [moreAboutUnit, setMoreAboutUnit] = useState({});
    const [reservationPriceCalculationDetails, setReservationPriceCalculationDetails] = useState(null);
    // Fetch loader-i.
    const [isLoadingInitialRequests, setIsLoadingInitialRequests] = useState(false);
    // "setIsLoadingUnitPriceCalculation" po default-u na true jer želimo prikaz skeletona dok se na CSR-u dohvaćaju podaci (API uklonjen sa SSR-a).
    const [isLoadingUnitPriceCalculation, setIsLoadingUnitPriceCalculation] = useState(true);
    const [isLoadingUnitPriceAvailabilityCalendar, setIsLoadingUnitPriceAvailabilityCalendar] = useState(false);
    const [isLoadingReviews, setIsLoadingReviews] = useState(false);
    const [isLoadingPresentationUnitAmenities, setIsLoadingPresentationUnitAmenities] = useState(false);
    const [isLoadingUnitMap, setIsLoadingUnitMap] = useState(false);
    const [isLoadingUnitMainGallery, setIsLoadingUnitMainGallery] = useState(false);
    const [isLoadingMoreAboutUnit, setIsLoadingMoreAboutUnit] = useState(false);
    const [isLoadingReservationPriceCalculationDetails, setIsLoadingReservationPriceCalculationDetails] = useState(false);
    // Fetch error-i.
    // Ovaj error se koristi samo za inicijalni fetch.
    // Fetch errori (s namjerom svaki fetch ima svoj error jer kod nekih fetch error-a korisnik može imati nesmetanu interakciju).
    // Primjer: puca fetch za dohvat recenzija, korisnik i dalje može napraviti rezervaciju, ne želimo mu prikazati globalni error layout.
    const [fetchError, setFetchError] = useState(null);
    // Kontrola prikaza broja box-ova za sobe
    const initialVisibleRooms = 4;
    const [showAllRoomDetailsRoomBoxes, setShowAllRoomDetailsRoomBoxes] = useState(false);
    const [visibleRoomDetailsRoomBoxes, setVisibleRoomDetailsRoomBoxes] = useState(initialVisibleRooms);
    // Kontrola prikaza broja kartica jedinica.
    const initialVisibleUnitCards = FacilityUnitContent.defaultVisibleUnitCards;
    const [showAllUnitCards, setShowAllUnitCards] = useState(false);
    const [visibleUnitCardIds, setVisibleUnitCardIds] = useState(ssrParameters?.visibleUnitCardIds || []);
    // Kontrola prikaza broja "distanceItems" (inicijalno se prikazuje onoliko koliko ima microlocation items-a ili 4 ako ih je manje od microlocation)
    const initialVisibleDistanceItems = presentationUnit?.location?.microlocation?.items?.length || 4;
    const [showAllDistanceItems, setShowAllDistanceItems] = useState(false);
    const [visibleDistanceItems, setVisibleDistanceItems] = useState(initialVisibleDistanceItems);
    // Kontrola prikaza aktivnih plaža(najbliže/ostale), po default-u prikazujemo najbliže
    const [activeLocationBeaches, setActiveLocationBeaches] = useState(CLOSEST_BEACH);
    const [activeSection, setActiveSection] = React.useState(null);
    // Kontrolni state-ovi za modale
    const [isOpenModalReviews, setIsOpenModalReviews] = useModalState(REVIEWS_MODAL_ID);
    const [isOpenModalUnitAmenities, setIsOpenModalUnitAmenities] = useModalState(DETAILS_MODAL_ID);
    const [isOpenModalUnitMap, setIsOpenModalUnitMap] = useModalState(FACILITY_MAP_MODAL_ID);
    const [isOpenUnitMainGallery, setIsOpenUnitMainGallery] = useModalState(GALLERY_MODAL_ID);
    const [isOpenSeeMoreAboutUnitModal, setIsOpenSeeMoreAboutUnitModal] = useModalState(UNIT_MODAL_ID);
    const [isOpenModalReservationPriceCalculationDetails, setIsOpenModalReservationPriceCalculationDetails] = useModalState(PAYMENT_DETAILS_MODAL_ID);
    const [currentRequestedUnitID, setCurrentRequestedUnitID] = useState(null);
    const [totalAdultsReservationParameters, setTotalAdultsReservationParameters] = useState(0);
    const [totalChildrenReservationParameters, setTotalChildrenReservationParameters] = useState(0);
    const [totalGuestsReservationParameters, setTotalGuestsReservationParameters] = useState(0);
    const [reservationButtonTextCTA, setReservationButtonTextCTA] = useState(null);
    const [trackerReservationTextCTA, setTrackerReservationTextCTA] = useState(null);
    const [selectedPersonNumber, setSelectedPersonNumber] = useState(ssrParameters?.selectedGuestsForMainUnit || 0);
    const [hasSelectedUnitsFromUnitPriceCalculation, setHasSelectedUnitsFromUnitPriceCalculation] = useState(false);
    const [selectedUnitsAlternative, setSelectedUnitsAlternative] = useState([]);
    // State-ovi za kontrolu "guest select" modala.
    const [modalUnitId, setModalUnitId] = useState(null);
    const [guestsModalUnitData, setGuestsModalUnitData] = useState(null);
    const [modalGuests, setModalGuests] = useState({
        adultsCount: 0,
        children12to17Count: 0,
        children4to11Count: 0,
        children0to3Count: 0,
    });
    const [sumAllReservationChildrens, setSumAllReservationChildrens] = useState(0);
    const [isOpenReservationMobileModal, setIsOpenReservationMobileModal] = useModalState(RESERVATION_MOBILE_MODAL_ID);
    const [isOpenGuestSelectModal, setIsOpenGuestSelectModal] = useModalState(GUEST_SELECT_MODAL_ID);
    // REZERVACIJSKI PODACIIIIIII
    const [reservationParameters, setReservationParameters] = useState(initialReservationParameters);
    const [lastUsedQueryString, setLastUsedQueryString] = useState(null);
    const [isReservationParamsSet, setIsReservationParamsSet] = useState(false);
    const [minVisibleDateOnDateRangePicker, setMinVisibleDateOnDateRangePicker] = useState(null);
    const [maxVisibleDateOnDateRangePicker, setMaxVisibleDateOnDateRangePicker] = useState(null);
    const [initialFocusedMonth, setInitialFocusedMonth] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const startDateFormatted = formatLocalizedDate(startDate, twoLetterISOLanguageCodeParameter);
    const endDateFormatted = formatLocalizedDate(endDate, twoLetterISOLanguageCodeParameter);
    const [monthAndYearOfFocusedMonth, setMonthAndYearOfFocusedMonth] = useState(null);
    const [firstMonthAndYearOfPresentationUnitPriceAvailabilityCalendarData, setFirstMonthAndYearOfPresentationUnitPriceAvailabilityCalendarData] = useState(null);
    const [lastMonthAndYearOfPresentationUnitPriceAvailabilityCalendarData, setLastMonthAndYearOfPresentationUnitPriceAvailabilityCalendarData] = useState(null);
    // Inicijalna vrijednost je mobile-first default koji odgovara CSS-u (.facility-unit__overview-mobile
    // je vidljiv po defaultu, .facility-unit__overview-mozaik-desktop-gallery-wrapper je display:none),
    // a stvarna vrijednost se odmah korigira u efektu ispod (getVisibleMonths/checkIsMobile) na osnovu
    // window.innerWidth, prije nego korisnik stigne bilo šta kliknuti.
    const [isMobileClient, setIsMobileClient] = useState(true);
    const [visibleNumberOfMonthsBasedOnInnerWidth, setVisibleNumberOfMonthsBasedOnInnerWidth] = useState(1);
    // Omogućava vanjsko kontroliranje modala unutar LoginForMoreBenefits komponente
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

    // REF-ovi
    const isIntervalFromInitiallySet = useRef(false);
    const isIntervalToInitiallySet = useRef(false);
    const reservationParametersUpdatePending = useRef(false);
    const reservationPriceCalculationDetailsFetchPending = useRef(false);
    const datePickerRef = useRef(null);
    const unitsShortInfoRef = useRef(null);
    const unitGalleryCarouselInnerRef = useRef(null);
    const unitGalleryCarouselNextBtn = useRef(null);
    const unitGalleryCarouselPrevBtn = useRef(null);
    const isSwipeOnMainMobileImage = useRef(false);
    // Zadnji mjesec koji je korisnik pogledao u kalendaru, bez re-rendera na svaku promjenu mjeseca.
    const focusedMonthDateForPriceAvailabilityCalendarRef = useRef(null);
    // Početna pozicija touch-a na mobilnoj galeriji - ref umjesto state-a touchstart/touchend - rerender zaštita.
    const touchStartPosition = useRef(null);

    // TODO: #58308 Privremeno, nakon comitt-a prve verzije će se uklonit ili doradit.
    // Razlog: Korišteno na staroj verziji, potrebno pregledat hoće li se koristit i na novoj.
    // eslint-disable-next-line
    const [prevStartDate, setPrevStartDate] = useState(null);
    // eslint-disable-next-line
    const [prevEndDate, setPrevEndDate] = useState(null);
    // numberOfMonthsForFetchBeforeAndAfterFocusedMonth služi za dohvat broja mjeseci prije i nakon mjeseca koji je u fokusu.
    const numberOfMonthsForFetchBeforeAndAfterFocusedMonth = 3;
    const [dateFromCurrentFocusedMonthForPriceAvailabilityCalendar, setDateFromCurrentFocusedMonthForPriceAvailabilityCalendar] = useState(null);
    // Moja ideja za highlighted sekcije, vidit jel pase dizajneru i ostalima.
    const highlightedDuration = 1200; // Usklađeno prema dizajnu sa stilom u animaciji za koju se css klasu koristi
    const [highlightDatePickerSection, setHighlightDatePickerSection] = useState(false);
    const [unitMainGallerySlides, setUnitMainGallerySlides] = useState([]);
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const unitMainGalleryRightArrow = 'unitMainGalleryRightArrowIdentificator';
    const unitMainGalleryLeftArrow = 'unitMainGalleryLeftArrowIdentificator';
    const [expandedRobinson, setExpandedRobinson] = useState(false);
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useModalState(FACILITY_UNIT_DESCRIPTION_MODAL_ID, BREAKPOINT_MD_PX);
    const initialWishlistedUnits = useMemo(() => {
        if (!presentationUnit || presentationUnit.isInWishlist === undefined) return null;
        return presentationUnit.isInWishlist ? [mainUnitID] : [];
    }, [presentationUnit, mainUnitID]);
    // Funkcije koje se direktno koriste u useEffect hook-ovima.
    const getPresentationUnit = () => {
        setIsLoadingInitialRequests(true);

        Promise.all([
            FacilityUnitService.getPresentationUnitFirstPartData(mainUnitID),
            FacilityUnitService.getPresentationUnitSecondPartData(mainUnitID),
            currentUser?.id && !IS_FRAME_SERVICE ? checkIsUnitWishlistedByUser(mainUnitID) : null,
        ])
            .then(([resFirst, resSecond, resThird]) => {
                if (
                    resFirst?.status === 200
                    && resSecond?.status === 200
                    && resFirst?.data
                    && resSecond?.data
                    && (resThird?.data || !currentUser?.id || IS_FRAME_SERVICE)
                ) {
                    const mergedData = {
                        ...resFirst.data,
                        ...resSecond.data,
                        isInWishlist: Boolean(resThird?.data?.isInWishlist),
                    };

                    setPresentationUnit(mergedData);

                    // Osiguranje da se dodaju strukturirani podaci ukoliko SSR ne funkcionira
                    injectService.injectStructuredData(
                        mergedData?.unitPresentationStructuredData,
                        pageConfig.facilityUnitPage.pageStructuredDataID,
                    );

                    return;
                }
                let erroredRes = null;
                if (resFirst && resFirst.status !== 200) {
                    erroredRes = resFirst;
                } else if (resSecond && resSecond.status !== 200) {
                    erroredRes = resSecond;
                } else if (currentUser?.id && !IS_FRAME_SERVICE && resThird && resThird.status !== 200) {
                    erroredRes = resThird;
                }
                reportError(erroredRes);
            })
            .catch((err) => {
                setFetchError(err);
                reportError(err);
            })
            .finally(() => {
                setIsLoadingInitialRequests(false);
            });
    };

    const trackingControls = useEcommerceTracking(reservationParameters, unitPriceCalculation, presentationUnit);
    const { triggerViewCart } = trackingControls;

    const getPresentationUnitPriceCalculation = (params = reservationParameters) => {
        setIsLoadingUnitPriceCalculation(true);
        const updatedSearchFormData = updateSearchFormDataFromSelectedUnits(params.selectedUnits);
        const updatedParams = {
            ...params,
            ...updatedSearchFormData,
        };
        const queryString = toQueryString(updatedParams);
        const currentSelectedGuestsForMainUnit = updatedParams.selectedUnits.length > 0 ? sumAllUnitGuests(updatedParams.selectedUnits[0]) : 0;
        FacilityUnitService.getPresentationUnitPriceCalculationInformation(`${mainUnitID}?${queryString}`)
            .then((res) => {
                if (res?.status === 200 && res?.data) {
                    setUnitPriceCalculation(res.data);
                    setSelectedPersonNumber((prevState) => {
                        if (prevState === currentSelectedGuestsForMainUnit) return prevState;
                        return currentSelectedGuestsForMainUnit;
                    });
                    return;
                }
                reportError(res);
            })
            .catch((err) => {
                reportError(err);
            })
            .finally(() => {
                setIsLoadingUnitPriceCalculation(false);
            });
    };

    const getPresentationUnitPriceAvailabilityCalendar = () => {
        setIsLoadingUnitPriceAvailabilityCalendar(true);
        const { intervalFrom } = reservationParameters;
        const parameterUnitID = mainUnitID;
        const parameterPersons = selectedPersonNumber;
        const parameterNumberOfMonths = numberOfMonthsForFetchBeforeAndAfterFocusedMonth;
        // Postavlja se:
        // - dateFromCurrentFocusedMonthForPriceAvailabilityCalendar ako korisnik lista misece kalednara.
        // - intervalFrom ako korisnik nije listao misece.
        const parameterFromMonth = focusedMonthDateForPriceAvailabilityCalendarRef.current || intervalFrom || null;
        // OČEKUJE: {unitId}/{persons}/{numberOfMonths}/{month}
        const completedDynamicEndpointPart = `${parameterUnitID}/${parameterPersons}/${parameterNumberOfMonths}${parameterFromMonth ? `/${parameterFromMonth}` : ''}`;
        FacilityUnitService.getPresentationUnitPriceAvailabilityCalendarData(completedDynamicEndpointPart)
            .then((res) => {
                if (res?.status === 200 && res?.data) {
                    setUnitPriceAvailabilityCalendar(res.data);
                    const monthsLength = res.data.months?.length ?? 0;
                    const lastElementMonth = monthsLength > 0 ? monthsLength - 1 : 0;
                    setFirstMonthAndYearOfPresentationUnitPriceAvailabilityCalendarData(getMonthAndYearFromISODate(res.data.months[0].days[0].date));
                    setLastMonthAndYearOfPresentationUnitPriceAvailabilityCalendarData(getMonthAndYearFromISODate(res.data.months[lastElementMonth].days[0].date));
                    // Ako request ide bez parametara intervala datuma backend vraća defaultni misec i godinu, ako su postavljeni parametri onda vraća od intervalaFrom.
                    setInitialFocusedMonth(new Date(res.data.selectedDefaultYear, res.data.selectedDefaultMonth - 1, 1)); // selectedDefaultMonth - 1 jer mjeseci za react-day-picker počinju od 0.
                    setMinVisibleDateOnDateRangePicker(res.data.firstActiveCalendarDate);
                    setMaxVisibleDateOnDateRangePicker(res.data.lastActiveCalendarDate);
                    return;
                }
                reportError(res);
            })
            .catch((err) => {
                reportError(err);
            })
            .finally(() => {
                setIsLoadingUnitPriceAvailabilityCalendar(false);
            });
    };

    const getMoreAboutUnit = () => {
        setIsLoadingMoreAboutUnit(true);
        FacilityUnitService
            .getPresentationUnitOverview(currentRequestedUnitID)
            .then((res) => {
                if (res?.status === 200 && res?.data) {
                    setMoreAboutUnit((prev) => ({
                        ...prev,
                        [currentRequestedUnitID]: res.data,
                    }));
                    return;
                }
                reportError(res);
            })
            .catch((err) => {
                setIsOpenUnitMainGallery(false);
                toast.error(axiosCustomErrors?.unavailableText);
                reportError(err);
            })
            .finally(() => {
                setIsLoadingMoreAboutUnit(false);
            });
    };

    const getUnitMainGallery = (directionForFirstSlide) => {
        setIsLoadingUnitMainGallery(true);
        FacilityUnitService
            .getPresentationUnitMainGalleryData(mainUnitID)
            .then((res) => {
                if (res?.status === 200 && res?.data) {
                    // INP: render svih slajdova nakon dohvata ide kao transition, da ga React može prekinuti
                    // kad korisnik u međuvremenu tapne (npr. ponovno na strelicu karusela).
                    startTransition(() => {
                        setUnitMainGallery(res.data);
                    });
                    const alwaysFirstImage = {
                        img: {
                            image: {
                                altText: presentationUnit.headerGallery.galleryItems.mainImage.altText,
                                sourceURL: presentationUnit.headerGallery.galleryItems.mainImage.sourceURL,
                                titleText: presentationUnit.headerGallery.galleryItems.mainImage.titleText,
                            },
                        },
                    };
                    const flatSlides = res.data.items.flatMap((item) => {
                        const { images, ...rest } = item;
                        return item.images.map((img) => ({
                            img,
                            additionalDataCreatedOnFront: rest,
                        }));
                    });
                    const allSlides = [alwaysFirstImage, ...flatSlides];
                    startTransition(() => {
                        if (directionForFirstSlide && directionForFirstSlide === unitMainGalleryLeftArrow) {
                            setCurrentSlideIndex(flatSlides.length);
                        }
                        if (directionForFirstSlide && directionForFirstSlide === unitMainGalleryRightArrow) {
                            setCurrentSlideIndex(1);
                        }
                        setUnitMainGallerySlides(allSlides);
                    });
                    return;
                }
                reportError(res);
            })
            .catch((err) => {
                setIsOpenUnitMainGallery(false);
                toast.error(axiosCustomErrors?.unavailableText);
                reportError(err);
            })
            .finally(() => {
                startTransition(() => {
                    setIsLoadingUnitMainGallery(false);
                });
            });
    };

    const parseQueryParams = (queryString) => {
        const user = userData.getDecodedUserDataFromLocalStorage();
        const queryParams = new URLSearchParams(queryString);
        const result = {};
        result.facilityId = mainFacilityID;
        // Affiliate logika
        const affiliateRootElement = document.getElementById('affiliateSiteId');
        if (affiliateRootElement) {
            const affiliateSiteId = affiliateRootElement.getAttribute('data-affiliatesiteid');
            if (affiliateSiteId > 0) {
                result.userId = user.id;
                result.affiliateSiteId = affiliateSiteId;
            }
        }
        // Intervali datuma
        const newIntervalFrom = queryParams.get('intervalFrom');
        const newIntervalTo = queryParams.get('intervalTo');
        if (newIntervalFrom) {
            const valueAsDate = parseISO(newIntervalFrom);
            const fromDate = isValid(valueAsDate) ? valueAsDate : null;
            isIntervalFromInitiallySet.current = !!fromDate;
            setStartDate(fromDate);
            setPrevStartDate(fromDate);
            queryParams.set('intervalFrom', fromDate ? newIntervalFrom : '');
        }

        if (newIntervalTo) {
            const valueAsDate = parseISO(newIntervalTo);
            const toDate = isValid(valueAsDate) ? valueAsDate : null;
            isIntervalToInitiallySet.current = !!toDate;
            setEndDate(toDate);
            setPrevEndDate(toDate);
            queryParams.set('intervalTo', toDate ? newIntervalTo : '');
        }
        // Promo recommendingPartnerOrPromoFlyer
        const newRecommendingPartnerOrPromoFlyer = queryParams.get('recommendingPartnerOrPromoFlyer');
        if (newRecommendingPartnerOrPromoFlyer) {
            queryParams.recommendingPartnerOrPromoFlyer = newRecommendingPartnerOrPromoFlyer;
        }
        // TODO: #58308 Privremeno, nakon comitt-a prve verzije će se uklonit ili doradit, linter se buni pa je napravljena alternativa.
        // queryParams.forEach((value, key) => {
        //     const path = [];
        //     key = key.replace(/\]/g, '');
        //     key.split('[').forEach((k) => path.push(k));
        //     let current = result;
        //     path.forEach((p, i) => {
        //         if (i === path.length - 1) {
        //             current[p] = value;
        //         } else {
        //             if (!current[p]) {
        //                 current[p] = /^\d+$/.test(path[i + 1]) ? [] : {};
        //             }
        //             current = current[p];
        //         }
        //     });
        // });
        queryParams.forEach((value, key) => {
            const path = [];
            const cleanedKey = key.replace(/\]/g, '');
            cleanedKey.split('[').forEach((k) => path.push(k));
            let current = result;
            path.forEach((p, i) => {
                if (i === path.length - 1) {
                    current[p] = value;
                } else {
                    if (!current[p]) {
                        current[p] = /^\d+$/.test(path[i + 1]) ? [] : {};
                    }
                    current = current[p];
                }
            });
        });
        // Normalizacija selectedUnits-a.
        if (Array.isArray(result.selectedUnits)) {
            result.selectedUnits = result.selectedUnits.map((unit) => ({
                unitId: unit.unitId ? parseInt(unit.unitId, 10) : 0,
                adultsCount: unit.adultsCount ? parseInt(unit.adultsCount, 10) : 0,
                children12to17Count: unit.children12to17Count ? parseInt(unit.children12to17Count, 10) : 0,
                children4to11Count: unit.children4to11Count ? parseInt(unit.children4to11Count, 10) : 0,
                children0to3Count: unit.children0to3Count ? parseInt(unit.children0to3Count, 10) : 0,
            }));
        }
        if (Array.isArray(result.selectedUnits) && result.selectedUnits.length > 0) {
            const transformedSelectedUnits = result.selectedUnits.map((unit) => ({
                adultsCount: Number(unit.adultsCount) || 0,
                children0to3Count: Number(unit.children0to3Count) || 0,
                children4to11Count: Number(unit.children4to11Count) || 0,
                children12to17Count: Number(unit.children12to17Count) || 0,
                unitId: unit.unitId,
            }));
            // Zamjenuto sa logikom updateSearchFormDataFromSelectedUnits.
            // TODO: #58308 Ako bude stvaralo problem vratit cu staru logiku, ako bude  sve u redu, uklanjam.
            // const searchFormAdultsCount = transformedSelectedUnits.reduce((total, unit) => total + unit.adultsCount, 0);
            // const searchFormChildren0to3Count = transformedSelectedUnits.reduce((total, unit) => total + unit.children0to3Count, 0);
            // const searchFormChildren4to11Count = transformedSelectedUnits.reduce((total, unit) => total + unit.children4to11Count, 0);
            // const searchFormChildren12to17Count = transformedSelectedUnits.reduce((total, unit) => total + unit.children12to17Count, 0);
            const createdSearchFormData = updateSearchFormDataFromSelectedUnits(transformedSelectedUnits);
            result.selectedUnits = transformedSelectedUnits;
            result.searchFormAdultsCount = createdSearchFormData.searchFormAdultsCount;
            result.searchFormChildren0to3Count = createdSearchFormData.searchFormChildren0to3Count;
            result.searchFormChildren4to11Count = createdSearchFormData.searchFormChildren4to11Count;
            result.searchFormChildren12to17Count = createdSearchFormData.searchFormChildren12to17Count;
        }

        return result;
    };

    const updateUrlWithParameters = () => {
        const existingParams = new URLSearchParams(window.location.search);
        const preserved = Array.from(existingParams.entries())
            .filter(([key]) => !key.startsWith('selectedUnits')
            && !['intervalFrom', 'intervalTo'].includes(key))
            .reduce((acc, [key, value]) => {
                acc[key] = value;

                return acc;
            }, {});
        const newQueryString = toQueryString({
            ...preserved,
            ...reservationParameters,
            intervalFrom: startDate ? formatDateForRequest(startDate) : undefined,
            intervalTo: endDate ? formatDateForRequest(endDate) : undefined,
        });
        const newUrl = `${window.location.pathname}?${newQueryString}`;
        window.history.pushState(null, '', newUrl);
    };

    // Glavni useEffect koji se trigger-a na load page-a.
    useEffect(() => {
        if (!presentationUnit) {
            getPresentationUnit();
        } else if (presentationUnit.isInWishlist === undefined && !IS_FRAME_SERVICE) {
            if (currentUser?.id) {
                checkIsUnitWishlistedByUser(mainUnitID)
                    .then((res) => {
                        setPresentationUnit({ ...presentationUnit, ...res.data });
                    })
                    .catch((err) => {
                        reportError(err);
                    });
            } else {
                setPresentationUnit({ ...presentationUnit, isInWishlist: false });
            }
        }
        const query = window.location.search;
        const parsedParams = parseQueryParams(query);
        setReservationParameters((prevState) => ({
            ...prevState,
            ...parsedParams,
            selectedUnits: parsedParams.selectedUnits ?? prevState.selectedUnits,
        }));
        setIsReservationParamsSet(true);
        disableElementService.removeServerSideRenderClass();
        // Ovaj dio je ispraćen sa stare verzije, razlog: footer na CSR-u je vidljiv samo ako je "window.isReactLoaded = true".
        window.isReactLoaded = true;
        // eslint-disable-next-line
    }, []);

    // useEffect triggera-a getPresentationUnitPriceCalculation tek nakon što se update-uju reservationParameters (na osnovu query parametara iz URL-a).
    // Preko ref-a didCallOnMountAfterUpdateFromQueryParams kontroliramo da se odradi samo jednom,
    // razlog: ne želimo nekontrolirani poziv getPresentationUnitPriceCalculation na svaki update reservationParameters
    const didCallOnMountAfterUpdateFromQueryParams = useRef(false);
    useEffect(() => {
        if (!didCallOnMountAfterUpdateFromQueryParams.current
            && reservationParameters.facilityId
        ) {
            // TODO: #58308 Privremeno, nakon comitt-a prve verzije će se uklonit ili doradit (razmislit od redoslijedu fetch-eva).
            if (!unitPriceCalculation) {
                getPresentationUnitPriceCalculation(reservationParameters);
            }
            didCallOnMountAfterUpdateFromQueryParams.current = true;
        }
        // eslint-disable-next-line
    }, [reservationParameters]);

    // Update-ujemo rezervacijske parametre na osnovu response-a od unitPriceCalculation.
    useEffect(() => {
        if (unitPriceCalculation?.units?.length > 0) {
            const selectedUnits = getSelectedUnitsFromApi(unitPriceCalculation.units);
            setSumAllReservationChildrens(sumAllReservationChildren(selectedUnits));
            setReservationParameters((prevState) => ({
                ...prevState,
                selectedUnits,
            }));
        }
    }, [unitPriceCalculation?.units]);

    // Uspoređujemo jedinice iz "presentationUnit.units.unitsShortInfo" i "unitPriceCalculation?.units" (samo kad nije u
    // tijeku fetch za detaljnu kalkulaciju "!isLoadingUnitPriceCalculation") zbog toga što dolaze sa dva različita API-a
    // i moze se dogoditi da zbog cache-a imaju razlike u response-u (npr. ako se kroz administraciju sakrije neka od jedinica).
    useEffect(() => {
        const presentationUnits = presentationUnit?.units?.unitsShortInfo;
        const unitsPriceCalculation = unitPriceCalculation?.units;
        if (isLoadingUnitPriceCalculation || !Array.isArray(presentationUnits) || !Array.isArray(unitsPriceCalculation)) {
            return;
        }
        if (presentationUnits.length === unitsPriceCalculation.length) {
            setMissingUnitIdsInUnitPriceCalculation([]);
            return;
        }
        const priceCalculationUnitIds = new Set(unitsPriceCalculation.map((unit) => String(unit.unitId)));
        const missingUnitIds = presentationUnits
            .filter((unit) => !priceCalculationUnitIds.has(String(unit.unitId)))
            .map((unit) => unit.unitId);
        setMissingUnitIdsInUnitPriceCalculation(missingUnitIds);
        reportError(
            new Error(
                'Postoje razlike u broju jedinica na API-ima (layout ne bi trebao pucati - za poznate bugove je kreiran silent fail #64135). '
                + 'Najčešći uzrok bug-a je mijenjanje vidljivosti jedinice kroz administraciju. '
                + 'Ako je to slučaj ili ako layout puca na load, treba pričekati da istekne cache ili flush-ati buckete za view i backend. '
                + 'Uzrok su sljedeće razlike: '
                + `Broj jedinica sa statičnog API-a - ${pageConfig.facilityUnitPage.presentationUnitSecondPartPath}: ${presentationUnits.length}, `
                + `Broj jedinica sa dinamičkog API-a - ${pageConfig.facilityUnitPage.presentationUnitPriceCalculationInformationPath}: ${unitsPriceCalculation.length}, `
                + `ID-evi jedinica koje nedostaju u ${pageConfig.facilityUnitPage.presentationUnitPriceCalculationInformationPath}: ${JSON.stringify(missingUnitIds)}`,
            ),
        );
    }, [isLoadingUnitPriceCalculation, unitPriceCalculation?.units, presentationUnit?.units?.unitsShortInfo]);

    const setSelectedUnitsOnFirstUpdateUnitPriceCalculation = useRef(false);
    useEffect(() => {
        if (setSelectedUnitsOnFirstUpdateUnitPriceCalculation.current) return;
        if (!unitPriceCalculation?.units || !mainUnitID) return;
        const selectedUnits = getSelectedUnitsFromApi(unitPriceCalculation.units);
        if (selectedUnits && selectedUnits.length > 0) {
            setReservationParameters((prevState) => ({
                ...prevState,
                selectedUnits,
            }));
        } else {
            const mainUnit = unitPriceCalculation.units.find((currentUnit) => currentUnit.unitId === mainUnitID);
            if (!mainUnit) return;
            reservationParametersUpdatePending.current = true;
            setReservationParameters((prevState) => ({
                ...prevState,
                selectedUnits: [
                    {
                        unitId: mainUnit.unitId,
                        adultsCount: mainUnit.adultsCount,
                        children0to3Count: mainUnit.children0to3Count,
                        children4to11Count: mainUnit.children4to11Count,
                        children12to17Count: mainUnit.children12to17Count,
                    },
                ],
            }));
        }
        // Postavljamo na true jer ovu logiku želimo samo na prvi update unitPriceCalculation.units.
        setSelectedUnitsOnFirstUpdateUnitPriceCalculation.current = true;
    }, [unitPriceCalculation?.units, mainUnitID]);

    useEffect(() => {
        if (reservationParametersUpdatePending.current && !isOpenReservationMobileModal && !isOpenGuestSelectModal) {
            updateUrlWithParameters();
            reservationParametersUpdatePending.current = false;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpenReservationMobileModal, isOpenGuestSelectModal]);

    // useEffect se kontrolirano triggera-a nakon update-a reservationParameters da bi na osnovu toga update-ali URL.
    const isFirstRunForUpdateQueryParams = useRef(true);
    useEffect(() => {
        // Ne update-ujemo parametre nakon što korisniku selektiramo jedinicu i dodamo goste u nju ako je došao bez parametara.
        // Razlog: Želimo link bez parametara ako korisnik nema odabranih parametara (našim "ručnim" odabirom selektirane jedinice
        // se ažurira reservationParameters i samim time se ažuriraju i query parametri u URL-u, zbog toga je dodana ova provjera).
        if (isFirstRunForUpdateQueryParams.current && setSelectedUnitsOnFirstUpdateUnitPriceCalculation.current) {
            isFirstRunForUpdateQueryParams.current = false;
            reservationParametersUpdatePending.current = false;
            return;
        }
        // Ne želimo da se url mijenja dok su modali otvoreni jer će se poremetiti back button funkcionalnost.
        if (reservationParametersUpdatePending.current && !isOpenReservationMobileModal && !isOpenGuestSelectModal) {
            updateUrlWithParameters();
            reservationParametersUpdatePending.current = false;
        }
        // eslint-disable-next-line
    }, [reservationParameters]);

    useEffect(() => {
        setHasSelectedUnitsFromUnitPriceCalculation(unitPriceCalculation?.units?.filter((unit) => unit.selected).length > 0);
        if (!reservationParameters.intervalFrom || !reservationParameters.intervalTo) {
            setReservationButtonTextCTA(presentationUnit?.simpleReservationFormLabels?.checkAvailabilityTitle);
            setTrackerReservationTextCTA('reservationFormCheckAvailabilityCTAClick');
            return;
        }
        if (
            reservationParameters.intervalFrom
            && reservationParameters.intervalTo
            && reservationParameters.selectedUnits[0]?.adultsCount < 0
            && !unitPriceCalculation?.simpleReservationFormCalculatedInformation?.totalPriceValueInEUR
        ) {
            setReservationButtonTextCTA(presentationUnit?.simpleReservationFormLabels?.checkAvailabilityTitle);
            setTrackerReservationTextCTA('reservationFormCheckAvailabilityCTAClick');
            return;
        }
        if (reservationParameters.selectedUnits[0]?.adultsCount <= 0) {
            setReservationButtonTextCTA(presentationUnit?.simpleReservationFormLabels?.addGuestsTitle);
            setTrackerReservationTextCTA('reservationFormAddPersonsCTAClick');
            return;
        }
        setReservationButtonTextCTA(null);
        setTrackerReservationTextCTA(null);
        // eslint-disable-next-line
    }, [unitPriceCalculation]);

    useEffect(() => {
        const { selectedUnits } = reservationParameters || {};
        const units = unitPriceCalculation?.units || [];
        if (Array.isArray(selectedUnits) && selectedUnits.length > 0 && units.length > 0) {
            const matched = selectedUnits.map((currentSelectedUnit) => units
                .find((currentUnit) => currentUnit.unitId === currentSelectedUnit.unitId))
                .filter(Boolean);
            setSelectedUnitsAlternative(matched);
        } else {
            setSelectedUnitsAlternative([]);
        }
    }, [unitPriceCalculation, reservationParameters]);

    useEffect(() => {
        if (isReservationParamsSet) {
            if (!unitPriceAvailabilityCalendar) {
                getPresentationUnitPriceAvailabilityCalendar();
                return;
            }
            if (isMonthOnEdgeOfLoadedCalendar(
                monthAndYearOfFocusedMonth,
                firstMonthAndYearOfPresentationUnitPriceAvailabilityCalendarData,
                lastMonthAndYearOfPresentationUnitPriceAvailabilityCalendarData,
            )) {
                getPresentationUnitPriceAvailabilityCalendar();
            }
        }
        // eslint-disable-next-line
    }, [isReservationParamsSet, dateFromCurrentFocusedMonthForPriceAvailabilityCalendar]);

    // Odvojeno jer ovaj trigger želimo samo na update selectedPersonNumber s uvjetom da postoje isReservationParamsSet && unitPriceAvailabilityCalendar (znaci da je incijalni request već prošao).
    useEffect(() => {
        if (isReservationParamsSet && unitPriceAvailabilityCalendar) {
            getPresentationUnitPriceAvailabilityCalendar();
        }
        // eslint-disable-next-line
    }, [isReservationParamsSet, selectedPersonNumber]);

    // Kontrola nad aktivnim sekcijama za aktivni menu item.
    useEffect(() => {
        const sectionIds = presentationUnit?.menu?.map((item) => item.sectionId) || [];
        const handleScroll = throttle(() => {
            let closestSection = null;
            let minDistance = Infinity;
            sectionIds.forEach((id) => {
                const el = document.getElementById(id);
                if (el) {
                    const rect = el.getBoundingClientRect();
                    if (rect.top >= 0 && rect.top < window.innerHeight) {
                        const distance = Math.abs(rect.top);
                        if (distance < minDistance) {
                            minDistance = distance;
                            closestSection = id;
                        }
                    }
                }
            });
            setActiveSection((prev) => (closestSection !== prev ? closestSection || null : prev));
        }, 300);
        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [presentationUnit?.menu]);

    // Kontrola nad togglerom za prikaz broja soba
    useEffect(() => {
        const numOfRooms = presentationUnit?.roomDetails?.rooms?.length || initialVisibleRooms;
        const visibleBoxes = showAllRoomDetailsRoomBoxes ? numOfRooms : initialVisibleRooms;
        setVisibleRoomDetailsRoomBoxes(visibleBoxes);
    }, [presentationUnit, showAllRoomDetailsRoomBoxes]);

    // Kontrola nad togglerom za prikaz broja item-a udaljenosti.
    useEffect(() => {
        const numOfDistanceItems = presentationUnit?.location?.distances?.distanceItems?.length || initialVisibleDistanceItems;
        const currentVisibleDistanceItems = showAllDistanceItems ? numOfDistanceItems : initialVisibleDistanceItems;
        setVisibleDistanceItems(currentVisibleDistanceItems);
        // eslint-disable-next-line
    }, [presentationUnit, showAllDistanceItems]);

    // Kontrola nad togglerom za prikaz broja kartica jedinica.
    useEffect(() => {
        if (unitPriceCalculation?.units?.length) {
            if (presentationUnit?.units?.unitsShortInfo?.length >= initialVisibleUnitCards) {
                setVisibleUnitCardIds(FacilityUnitContent.getInitialVisibleUnitCardIds(unitPriceCalculation, initialVisibleUnitCards));
            } else {
                // Prikazujemo sve jedinice ako broj jedinica nije veći ili jednak od limita za inicijalni prikaz.
                setShowAllUnitCards(true);
            }
        }
        // eslint-disable-next-line
    }, [presentationUnit?.units?.unitsShortInfo, unitPriceCalculation?.units]);

    // Kontrola nad dohvatom detalja za trenutno kliknutu jedinicu.
    useEffect(() => {
        const iscurrentRequestedUnitIDValidNum = currentRequestedUnitID && typeof currentRequestedUnitID === 'number' && !Number.isNaN(currentRequestedUnitID);
        if (isOpenSeeMoreAboutUnitModal && iscurrentRequestedUnitIDValidNum && !moreAboutUnit[currentRequestedUnitID]) {
            getMoreAboutUnit();
        }
        // eslint-disable-next-line
    }, [isOpenSeeMoreAboutUnitModal, currentRequestedUnitID]);

    // Trigger-ujemo update zbroja gostiju nakon update-a reservationParameters.
    useEffect(() => {
        if (reservationParameters.selectedUnits?.length > 0) {
            const totals = reservationParameters.selectedUnits.reduce(
                (acc, unit) => {
                    acc.adults += Number(unit.adultsCount) || 0;
                    acc.children += (Number(unit.children0to3Count) || 0) + (Number(unit.children4to11Count) || 0) + (Number(unit.children12to17Count) || 0);
                    return acc;
                },
                { adults: 0, children: 0 },
            );
            setTotalAdultsReservationParameters(totals.adults);
            setTotalChildrenReservationParameters(totals.children);
            setTotalGuestsReservationParameters(totals.adults + totals.children);
        } else {
            setTotalAdultsReservationParameters(0);
            setTotalChildrenReservationParameters(0);
            setTotalGuestsReservationParameters(0);
        }
    }, [reservationParameters]);

    // isMobileClient i broj vidljivih mjeseci se u potpunosti određuju na klijentu na osnovu window.innerWidth.
    // useLayoutEffect (umjesto useEffect) se izvršava sinhrono prije browser paint-a, pa se mobile-first
    // default iz useState-a gore ispravi prije nego korisnik uopće vidi render, bez potrebe za bilo kakvim
    // podatkom s backenda/SSR-a.
    useLayoutEffect(() => {
        function getVisibleMonths() {
            return window.innerWidth < mobileBreakPoint ? 1 : 2;
        }
        function checkIsMobile() {
            return window.innerWidth < mobileBreakPoint;
        }
        setVisibleNumberOfMonthsBasedOnInnerWidth(getVisibleMonths());
        setIsMobileClient(checkIsMobile());
        const handleResize = () => {
            setVisibleNumberOfMonthsBasedOnInnerWidth(getVisibleMonths());
            setIsMobileClient(checkIsMobile());
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // TODO: #58308 Pod komentarom logika za focus na misec koji user selektira,
    // razlog: ne zelimo vraćanje na default-ni focus ako korisnik ukloni range van defaultnog miseca za focus.
    // Uklonit ću kod ako shvatimo da se nećemo vraćati na ovu logiku.
    // useEffect(() => {
    //     const targetMonth = reservationParameters?.intervalFrom
    //         ? startOfMonth(parseISO(reservationParameters.intervalFrom))
    //         : startOfMonth(defaultCalendarMonth);
    //     setCalendarStartingMonth(targetMonth);
    // // eslint-disable-next-line
    // }, [reservationParameters]);

    // useMemo hook-ovi.
    const availabilityDates = useMemo(() => {
        if (!unitPriceAvailabilityCalendar?.months) return [];
        return unitPriceAvailabilityCalendar.months.flatMap((month) => month.days.map((day) => ({
            ...day,
            monthName: month.monthName,
        })));
    }, [unitPriceAvailabilityCalendar]);

    const unitPriceCalculationUnitsById = React.useMemo(() => {
        const map = {};
        if (unitPriceCalculation && unitPriceCalculation.units) {
            unitPriceCalculation.units.forEach((unit) => {
                map[unit.unitId] = { ...unit };
            });
        }
        return map;
    }, [unitPriceCalculation]);

    const mainUnitDataForWishlistWidget = useMemo(() => {
        const unitShortInfo = presentationUnit?.units?.unitsShortInfo?.find((unit) => unit.unitId === mainUnitID);
        if (!unitShortInfo) return null;
        return {
            unitId: mainUnitID,
            title: unitShortInfo.unitShortTitle,
            type: unitShortInfo.type,
        };
    }, [presentationUnit, mainUnitID]);

    // Ovo nam za sada nije potrebno zbog nove logike sa mogućnosti uklanjanja glavne jedinice, (uklonit ću naknadno).
    // const isMainPresentationUnitById = React.useMemo(() => {
    //     const map = {};
    //     if (presentationUnit && presentationUnit.units && presentationUnit.units.unitsShortInfo) {
    //         presentationUnit.units.unitsShortInfo.forEach((unit) => {
    //             map[unit.unitId] = !!unit.isMainUnit;
    //         });
    //     }
    //     return map;
    // }, [presentationUnit]);

    // Share-ani podaci kroz komponente.
    const sharedUnitData = useMemo(() => ({
        selectedUnitText: presentationUnit?.units?.selectedUnitText || '',
        addUnitToReservationText: presentationUnit?.units?.addUnitToReservationText || '',
        removeUnitFromReservationText: presentationUnit?.units?.removeUnitFromReservationText || '',
        moreDetailsText: presentationUnit?.units?.moreDetailsText || '',
        showAllUnitsText: presentationUnit?.units?.showAllUnitsText || '',
        showLessUnitsText: presentationUnit?.units?.showLessUnitsText || '',
        simpleReservationFormLabels: presentationUnit?.simpleReservationFormLabels || '',
        numberOfGuestsForm: presentationUnit?.numberOfGuestsForm || '',
        simpleReservationFormCalculatedInformation: unitPriceCalculation?.simpleReservationFormCalculatedInformation || '',
        selectedIntervalNotAvailable: presentationUnit?.pricelistAndAvailability?.selectedIntervalNotAvailable || '',
        notAllowedToSelectMessage: presentationUnit?.pricelistAndAvailability?.notAllowedToSelectMessage || '',
        mainUnitID,
        // eslint-disable-next-line
    }), [presentationUnit, unitPriceCalculation]);

    const openGuestSelectModal = (unit) => {
        const matchedUnitData = presentationUnit.units.unitsShortInfo.find(
            (shortInfo) => shortInfo.unitId === unit.unitId,
        );
        if (matchedUnitData) {
            setGuestsModalUnitData(matchedUnitData);
        } else {
            setGuestsModalUnitData(null);
        }
        setModalUnitId(unit.unitId);
        setModalGuests({
            adultsCount: unit.adultsCount,
            children12to17Count: unit.children12to17Count,
            children4to11Count: unit.children4to11Count,
            children0to3Count: unit.children0to3Count,
        });
        setIsOpenGuestSelectModal(true);
    };

    const scrollIntoDatePickerView = (activeHighlight = true) => {
        if (!datePickerRef.current) return;

        const rect = datePickerRef.current.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const target = Math.max(0, rect.top + scrollTop - heightToScrollIntoView);

        const urlParams = new URLSearchParams(window.location.search);
        const isAdriaticAI = urlParams.get('adriaticAI');
        const isAIFrameService = isAdriaticAI === '1';

        const scrollHandler = () => {
            if (IS_FRAME_SERVICE) {
                setScrollTop(target);
            } else if (isAIFrameService) {
                window.scrollTo({ top: target, behavior: 'smooth' });
            } else {
                window.parent.scrollTo({ top: target, behavior: 'smooth' });
            }
        };

        scrollHandler();

        if (activeHighlight) {
            setHighlightDatePickerSection(true);
            setTimeout(() => setHighlightDatePickerSection(false), highlightedDuration);
        }
    };

    const scrollIntoUnitsShortInfoView = (e) => {
        e?.stopPropagation();
        // unitsShortInfoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (unitsShortInfoRef.current) {
            const rect = unitsShortInfoRef.current.getBoundingClientRect();
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            window.scrollTo({
                top: rect.top + scrollTop - heightToScrollIntoView,
                behavior: 'smooth',
            });
        }
    };

    // Handler-i
    const handleUnitGalleryCarouselClick = (direction) => {
        if (!unitMainGallery) {
            // INP: prvi klik na strelicu pokreće dohvat galerije i re-render cijele stranice, pa ide nakon painta.
            runAfterNextPaint(() => getUnitMainGallery(direction));
        }
    };

    const handleTouchStart = (e) => {
        isSwipeOnMainMobileImage.current = false;
        touchStartPosition.current = {
            x: e.changedTouches[0].screenX,
            y: e.changedTouches[0].screenY,
        };
    };

    const detectSwipeDirection = (endX, endY) => {
        if (!touchStartPosition.current) return;
        const diffX = touchStartPosition.current.x - endX;
        const diffY = touchStartPosition.current.y - endY;
        const absX = Math.abs(diffX);
        const absY = Math.abs(diffY);
        const threshold = 50;
        // Triggera se samo ako je horizontalni swipe veći od vertikalnog.
        if (absX > threshold && absX > absY) {
            isSwipeOnMainMobileImage.current = true;
            if (diffX > 0) {
                unitGalleryCarouselNextBtn.current?.click();
            } else {
                unitGalleryCarouselPrevBtn.current?.click();
            }
        }
        touchStartPosition.current = null;
    };

    const handleTouchEnd = (e) => {
        if (!touchStartPosition.current) return;
        const endX = e.changedTouches[0].screenX;
        const endY = e.changedTouches[0].screenY;
        detectSwipeDirection(endX, endY);
    };

    const handleDatesChange = (newStartDate, newEndDate) => {
        setStartDate(newStartDate);
        setEndDate(newEndDate);
        const intervalFrom = newStartDate ? format(newStartDate, 'yyyy-MM-dd') : '';
        const intervalTo = newEndDate ? format(newEndDate, 'yyyy-MM-dd') : '';
        const hasReservationUrl = !!unitPriceCalculation?.simpleReservationFormCalculatedInformation?.createReservationButtonURL;
        if (!newStartDate && !newEndDate) {
            reservationParametersUpdatePending.current = true;
            const updated = {
                ...reservationParameters,
                intervalFrom: '',
                intervalTo: '',
            };
            setReservationParameters(updated);
            getPresentationUnitPriceCalculation(updated);
            return;
        }
        if (hasReservationUrl) {
            const updated = {
                ...reservationParameters,
                intervalFrom,
                intervalTo,
            };
            setReservationParameters(updated);
            getPresentationUnitPriceCalculation(updated);
        } else if (newStartDate && newEndDate) {
            reservationParametersUpdatePending.current = true;
            const updated = {
                ...reservationParameters,
                intervalFrom,
                intervalTo,
            };
            if (updated.selectedUnits?.length > 0) {
                // Ako je broj odraslih(adultsCount) gostiju u prvoj jedinici(glavnoj) 0, otvaramo modal za odabir gostju za nju.
                if (updated.selectedUnits[0]?.adultsCount <= 0) {
                    const unitForGuestSelect = updated.selectedUnits[0];
                    runAfterNextPaint(() => openGuestSelectModal(unitForGuestSelect));
                }
            } else {
                // Ako ne postoji selektirana jedinica, otvaramo modal za odabir gostiju prve jedinice iz unitPriceCalculation.
                const unitForGuestSelect = unitPriceCalculation?.units?.[0];
                runAfterNextPaint(() => openGuestSelectModal(unitForGuestSelect));
            }
            setReservationParameters(updated);
            getPresentationUnitPriceCalculation(updated);
            if (!IS_FRAME_SERVICE) dispatchTrackingEvent(RESERVATION_FORM_DATES_ON_CALENDAR_SELECTED);
        }
    };

    const handleSelectSuggestedDates = (suggestedDateFrom, suggestedDateTo) => {
        const safeStartDate = typeof suggestedDateFrom === 'string'
            ? parse(suggestedDateFrom, 'yyyy-MM-dd', new Date())
            : suggestedDateFrom;
        const safeEndDate = typeof suggestedDateTo === 'string'
            ? parse(suggestedDateTo, 'yyyy-MM-dd', new Date())
            : suggestedDateTo;
        setStartDate(safeStartDate);
        setEndDate(safeEndDate);
        if (safeStartDate && safeEndDate) {
            const intervalFrom = format(safeStartDate, 'yyyy-MM-dd');
            const intervalTo = format(safeEndDate, 'yyyy-MM-dd');
            reservationParametersUpdatePending.current = true;
            const updated = {
                ...reservationParameters,
                intervalFrom,
                intervalTo,
            };
            setReservationParameters(() => {
                // Ako je broj odraslih(adultsCount) gostiju u prvoj jedinici(glavnoj) 0, otvaramo modal za odabir gostju za nju.
                if (updated.selectedUnits[0]?.adultsCount <= 0) {
                    openGuestSelectModal(updated.selectedUnits[0]);
                }
                return updated;
            });
            getPresentationUnitPriceCalculation(updated);
            scrollIntoDatePickerView(false);
        }
        if (!IS_FRAME_SERVICE) dispatchTrackingEvent(UNIT_PRESENTATION_LAST_MINUTE_INTERVAL_SELECT, { unitID: mainUnitID });
    };

    const handleCalendarCurrentMonth = (newMonth) => {
        const formattedDate = format(newMonth, 'yyyy-MM-dd');
        const newMonthAndYear = getMonthAndYearFromISODate(formattedDate);
        focusedMonthDateForPriceAvailabilityCalendarRef.current = formattedDate;
        // INP: state mijenjamo samo kad treba dohvatiti nove mjesece; ostali se prikazuju iz već učitanih podataka.
        const isFetchNeeded = !unitPriceAvailabilityCalendar || isMonthOnEdgeOfLoadedCalendar(
            newMonthAndYear,
            firstMonthAndYearOfPresentationUnitPriceAvailabilityCalendarData,
            lastMonthAndYearOfPresentationUnitPriceAvailabilityCalendarData,
        );
        if (!isFetchNeeded) return;
        setDateFromCurrentFocusedMonthForPriceAvailabilityCalendar(formattedDate);
        setMonthAndYearOfFocusedMonth(newMonthAndYear);
    };

    const getUnitReviews = () => {
        setIsLoadingReviews(true);
        FacilityUnitService
            .getFacilityUnitReviewsData(mainFacilityID)
            .then((res) => {
                if (res?.status === 200 && res?.data) {
                    setFacilityUnitReviews(res.data);
                    return;
                }
                reportError(res);
            })
            .catch((err) => {
                setIsOpenModalReviews(false);
                toast.error(axiosCustomErrors?.unavailableText);
                reportError(err);
            })
            .finally(() => {
                setIsLoadingReviews(false);
            });
    };

    const getPresentationUnitAmenities = () => {
        setIsLoadingPresentationUnitAmenities(true);
        FacilityUnitService
            .getPresentationUnitAmenitiesData(mainUnitID)
            .then((res) => {
                if (res?.status === 200 && res?.data) {
                    setPresentationUnitAmenities(res.data);
                    return;
                }
                reportError(res);
            })
            .catch((err) => {
                setIsOpenModalUnitAmenities(false);
                toast.error(axiosCustomErrors?.unavailableText);
                reportError(err);
            })
            .finally(() => {
                setIsLoadingPresentationUnitAmenities(false);
            });
    };

    const getUnitMap = () => {
        setIsLoadingUnitMap(true);
        FacilityUnitService
            .getFacilityMapOfInterestsData(mainFacilityID)
            .then((res) => {
                if (res?.status === 200 && res?.data) {
                    setUnitMap(res.data);
                    return;
                }
                reportError(res);
            })
            .catch((err) => {
                setIsOpenModalUnitMap(false);
                toast.error(axiosCustomErrors?.unavailableText);
                reportError(err);
            })
            .finally(() => {
                setIsLoadingUnitMap(false);
            });
    };

    const getReservationPriceCalculationDetails = () => {
        if (!unitPriceCalculation?.simpleReservationFormCalculatedInformation?.totalPriceValueInEUR) return;
        const queryString = toQueryString(reservationParameters);
        if (lastUsedQueryString === queryString) return;
        setIsLoadingReservationPriceCalculationDetails(true);
        FacilityUnitService
            .getReservationPriceCalculationDetailsData(`?${queryString}`)
            .then((res) => {
                if (res?.status === 200 && res?.data) {
                    setReservationPriceCalculationDetails(res.data);
                    return;
                }
                reportError(res);
            })
            .catch((err) => {
                setIsOpenModalReservationPriceCalculationDetails(false);
                toast.error(axiosCustomErrors?.unavailableText);
                reportError(err);
            })
            .finally(() => {
                setIsLoadingReservationPriceCalculationDetails(false);
                setLastUsedQueryString(queryString);
            });
    };

    useEffect(() => {
        if (unitPriceCalculation) {
            if (isOpenReservationMobileModal || isOpenModalReservationPriceCalculationDetails) {
                getReservationPriceCalculationDetails();
            } else {
                reservationPriceCalculationDetailsFetchPending.current = true;
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unitPriceCalculation]);

    const scrollToSection = (id) => {
        const section = document.getElementById(id);
        if (section) {
            const topOffset = heightToScrollIntoView;
            const elementPosition = section.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementPosition - topOffset;
            if (IS_FRAME_SERVICE) {
                setScrollTop(elementPosition);
            } else {
                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth',
                });
            }
            if (!IS_FRAME_SERVICE) dispatchTrackingEvent(UNIT_PRESENTATION_QUICK_MENU_SELECT_SECTION, { unitPresentationQuickMenuItem: id });
        }
    };

    const removeUnitFromSelectedUnitsInReservationarameters = (unitData) => {
        const updatedUnits = reservationParameters.selectedUnits.filter((u) => u.unitId !== unitData.unitId);
        const updated = {
            ...reservationParameters,
            selectedUnits: updatedUnits,
        };
        reservationParametersUpdatePending.current = true;
        setReservationParameters(updated);
        getPresentationUnitPriceCalculation(updated);
        if (isOpenSeeMoreAboutUnitModal) {
            setIsOpenSeeMoreAboutUnitModal(false);
        }
    };

    useEffect(() => {
        if ((isOpenModalReservationPriceCalculationDetails || isOpenReservationMobileModal) && reservationPriceCalculationDetailsFetchPending.current) {
            getReservationPriceCalculationDetails();
            reservationPriceCalculationDetailsFetchPending.current = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpenModalReservationPriceCalculationDetails, isOpenReservationMobileModal]);

    const handleOpenReservationMobileModal = () => {
        // TODO: #58308 Privremeno, nakon comitt-a prve verzije će se uklonit ili doradit.
        setIsOpenReservationMobileModal(true);
        if (!IS_FRAME_SERVICE) dispatchTrackingEvent(RESERVATION_FORM_MOBILE_DOCK_OPEN, { unitID: mainUnitID });
    };

    const closeReservationMobileModal = () => {
        setIsOpenReservationMobileModal(false);
    };

    const closeGuestSelectModal = () => {
        setIsOpenGuestSelectModal(false);
    };

    const confirmGuestSelectModal = () => {
        setIsOpenGuestSelectModal(false);
        reservationParametersUpdatePending.current = true;
        const exists = reservationParameters.selectedUnits.some((unit) => unit.unitId === modalUnitId);
        let updatedUnits;
        if (exists) {
            updatedUnits = reservationParameters.selectedUnits.map((unit) => {
                if (unit.unitId === modalUnitId) {
                    return { ...unit, ...modalGuests };
                }
                return unit;
            });
        } else {
            updatedUnits = [
                ...reservationParameters.selectedUnits,
                {
                    unitId: modalUnitId,
                    ...modalGuests,
                },
            ];
        }
        const updated = {
            ...reservationParameters,
            selectedUnits: updatedUnits,
        };
        setReservationParameters(updated);
        getPresentationUnitPriceCalculation(updated);
    };

    useEffect(() => {
        if (!facilityUnitReviews && !isLoadingReviews && isOpenModalReviews) {
            getUnitReviews();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpenModalReviews]);

    const handleReviewsModal = (openFrom) => {
        const isOpen = !isOpenModalReviews;
        if (isOpen) {
            if (!IS_FRAME_SERVICE && openFrom) {
                if (openFrom === UNIT_PRESENTATION_REVIEWS_FROM_GRADE_OPEN) {
                    dispatchTrackingEvent(UNIT_PRESENTATION_REVIEWS_FROM_GRADE_OPEN, { unitID: mainUnitID });
                }
                if (openFrom === UNIT_PRESENTATION_REVIEWS_FROM_SHOW_MORE_OPEN) {
                    dispatchTrackingEvent(UNIT_PRESENTATION_REVIEWS_FROM_SHOW_MORE_OPEN, { unitID: mainUnitID });
                }
            }
        }
        runAfterNextPaint(() => {
            setIsOpenModalReviews(isOpen);
        });
    };

    useEffect(() => {
        if (!presentationUnitAmenities && !isLoadingPresentationUnitAmenities && isOpenModalUnitAmenities) {
            getPresentationUnitAmenities();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpenModalUnitAmenities]);

    const handleUnitAmenitiesModal = () => {
        const isOpen = !isOpenModalUnitAmenities;
        if (isOpen && !IS_FRAME_SERVICE) {
            dispatchTrackingEvent(UNIT_PRESENTATION_AMENITY_DETAILS_OPEN, { unitID: mainUnitID });
        }
        runAfterNextPaint(() => {
            setIsOpenModalUnitAmenities(isOpen);
        });
    };

    useEffect(() => {
        if (!unitMap && !isLoadingUnitMap && isOpenModalUnitMap) {
            getUnitMap();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpenModalUnitMap]);

    const handleUnitMapModal = (openFrom) => {
        const isOpen = !isOpenModalUnitMap;
        if (isOpen) {
            if (!IS_FRAME_SERVICE && openFrom) {
                if (openFrom === UNIT_PRESENTATION_MAP_LOCATION_FROM_OVERVIEW_OPEN) {
                    dispatchTrackingEvent(UNIT_PRESENTATION_MAP_LOCATION_FROM_OVERVIEW_OPEN, { unitID: mainUnitID });
                }
                if (openFrom === UNIT_PRESENTATION_MAP_LOCATION_FROM_LOCATION_OPEN) {
                    dispatchTrackingEvent(UNIT_PRESENTATION_MAP_LOCATION_FROM_LOCATION_OPEN, { unitID: mainUnitID });
                }
            }
        }
        runAfterNextPaint(() => {
            setIsOpenModalUnitMap(isOpen);
        });
    };

    useEffect(() => {
        if (!unitMainGallery && !isLoadingUnitMainGallery && isOpenUnitMainGallery) {
            getUnitMainGallery();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpenUnitMainGallery]);

    const handleUnitMainGalleryModal = () => {
        const isOpen = !isOpenUnitMainGallery;
        if (isOpen) {
            setIsOpenUnitMainGallery(true);
            if (!IS_FRAME_SERVICE) dispatchTrackingEvent(UNIT_PRESENTATION_MAIN_GALLERY_OPEN, { unitID: mainUnitID });
        } else {
            setIsOpenUnitMainGallery(false);
        }
    };

    // Metoda provjerava da li se radi o swipe-u ili kliku, razlog: implementacija mogućnosti fetch-a putem swipe-a preko područja strelica.
    const handleMobileClickUnitMainGallery = (e) => {
        if (isSwipeOnMainMobileImage.current) {
            e.preventDefault();
            return;
        }
        runAfterNextPaint(handleUnitMainGalleryModal);
    };

    const handleSeeMoreAboutUnitModal = (id) => {
        setIsOpenSeeMoreAboutUnitModal(!isOpenSeeMoreAboutUnitModal);
        setCurrentRequestedUnitID(id);
    };

    const handlePaymentDetailsModal = () => {
        const isOpen = !isOpenModalReservationPriceCalculationDetails;
        if (isOpen && !IS_FRAME_SERVICE) {
            dispatchTrackingEvent(RESERVATION_FORM_DETAILED_PRICE_CALCULATION, { unitID: mainUnitID });
        }
        setIsOpenModalReservationPriceCalculationDetails(isOpen);
    };

    const handleSelectAnotherUnit = (unitData) => {
        const exists = reservationParameters.selectedUnits.some((u) => u.unitId === unitData.unitId);

        if (exists) {
            removeUnitFromSelectedUnitsInReservationarameters(unitData);
        } else {
            openGuestSelectModal(unitData);
        }
        // TODO: #58308 Privremeno, nakon comitt-a prve verzije će se uklonit ili doradit.
        // Zatvaramo modal detalja jedinice.
        setIsOpenSeeMoreAboutUnitModal(false);
    };

    // TODO: #58308 Privremeno, nakon comitt-a prve verzije će se uklonit ili doradit.
    // TODO: Triba dodat scrol na start minus dio koliki je height navigacije koja je sticky na vrhu, ovo bi bilo dobro
    // stavit varijablu height navigacije i to koristiti u ovakvim situacijama, da se ne mora razmisljat zasto je postavljeno npr 160px.
    const handleReservationFormClickDates = () => {
        scrollIntoDatePickerView();
        if (!IS_FRAME_SERVICE) dispatchTrackingEvent(RESERVATION_FORM_GLOBAL_DATES_SELECTED);
        // TODO: #58308 Privremeno, nakon comitt-a prve verzije će se uklonit ili doradit (triba raspravit sa ostalima).
        // if (!startDate || !endDate) {
        //     scrollIntoDatePickerView();
        // } else if (presentationUnit?.units?.unitsShortInfo?.length > 1) {
        //     scrollIntoUnitsShortInfoView();
        // } else {
        //     // Ne postoje kartice jedinica jer imamo samo jednu i za nju otvaramo modal za odabir gostiju.
        //     openGuestSelectModal(unitPriceCalculation.units[0]);
        // }
    };

    // Dogovoreno da će klik na select gostiju otvarati modal za odabir gostiju glavne(prve) jedinice.
    const handleReservationFormClickGuests = () => {
        if (!IS_FRAME_SERVICE) dispatchTrackingEvent(RESERVATION_FORM_GLOBAL_PERSONS_SELECTED);
        openGuestSelectModal(unitPriceCalculation.units[0]);
    };

    const handleClickStartReservation = () => {
        const adultsCount = reservationParameters.selectedUnits?.[0]?.adultsCount ?? 0;
        const totalPrice = unitPriceCalculation?.simpleReservationFormCalculatedInformation?.totalPriceValueInEUR || false;
        const areDatesSelected = startDate && endDate;
        if (!areDatesSelected || (areDatesSelected && adultsCount > 0 && !totalPrice)) {
            if (isOpenReservationMobileModal) {
                setIsOpenReservationMobileModal(false);
            }
            scrollIntoDatePickerView();
            return;
        }
        openGuestSelectModal(unitPriceCalculation?.units?.[0]);
    };

    const handleTrackerReservationTextCTA = () => {
        if (!IS_FRAME_SERVICE && trackerReservationTextCTA) {
            dispatchTrackingEvent(trackerReservationTextCTA, { unitID: mainUnitID });
        }
    };

    // Views Layout-i
    const renderDateRangePickerView = () => (
        <DateRangePicker
            focusedMonth={initialFocusedMonth}
            availabilityDates={availabilityDates}
            startDate={startDate}
            endDate={endDate}
            visibleNumberOfMonths={visibleNumberOfMonthsBasedOnInnerWidth}
            onDatesChange={handleDatesChange}
            minDate={parseISO(minVisibleDateOnDateRangePicker)}
            maxDate={parseISO(maxVisibleDateOnDateRangePicker)}
            orientation={orientation}
            notAllowedToSelectMessage={presentationUnit?.pricelistAndAvailability?.notAllowedToSelectMessage}
            onMonthChange={handleCalendarCurrentMonth}
            ssrParameters={ssrParameters}
            isMobileClient={isMobileClient}
            customHorizontalScroll={true}
            validationHighlight={highlightDatePickerSection}
        />
        // <DateRangePicker
        //     parentElementID="facilityUnitPicker"
        //     initialMonth={calendarStartingMonth}
        //     availabilityDates={availabilityDates}
        //     startDate={startDate}
        //     endDate={endDate}
        //     visibleNumberOfMonths={visibleNumberOfMonthsBasedOnInnerWidth}
        //     onDatesChange={handleDatesChange}
        //     minDate={startOfToday()}
        //     maxDate={parseISO(maxVisibleDateOnDateRangePicker)}
        //     orientation={orientation}
        //     customLegend={presentationUnit.pricelistAndAvailability.calendarDayStatuses}
        //     onMonthChange={handleCalendarCurrentMonth}
        //     disableDaySelect={disableDaySelect}
        //     ssrParameters={ssrParameters}
        // />
    );

    const renderBeachContentView = (beachData) => {
        if (!beachData) return null;

        return (
            <div className="facility-unit__location-beaches-content d-flex flex-column flex-md-row">
                {beachData.images?.length > 0 && (
                    <div className="facility-unit__location-beaches-content-slider">
                        <Carousel
                            data={{ items: beachData.images }}
                            autoplay={false}
                            imageWidth={365}
                            imageHeight={240}
                            currentID="FacilityUnitContentBeachSlider"
                            firstImageLazy={true}
                            trackingEvents={{
                                beachGallery: {
                                    activeLocationBeaches,
                                    mainTextForMatch: CLOSEST_BEACH,
                                    mainOnNext: UNIT_PRESENTATION_MAIN_BEACH_GALLERY_NEXT,
                                    otherTextForMatch: OTHER_BEACHES,
                                    otherOnNext: UNIT_PRESENTATION_OTHER_BEACHES_GALLERY_NEXT,
                                },
                            }}
                        />
                    </div>
                )}
                <div className="facility-unit__location-beaches-content-info d-flex flex-column">
                    <div className="facility-unit__location-beaches-content-info__group">
                        <h4 className="facility-unit__location-beaches-content-info-title">{beachData.beachTypeTitle}</h4>
                        <p className="facility-unit__location-beaches-content-info-txt">{beachData.beachTypeValue}</p>
                    </div>
                    <div className="facility-unit__location-beaches-content-info__group">
                        <h4 className="facility-unit__location-beaches-content-info-title">{beachData.seaEntranceTitle}</h4>
                        <p className="facility-unit__location-beaches-content-info-txt">{beachData.seaEntranceValue}</p>
                    </div>
                    <div className="facility-unit__location-beaches-content-info-items d-flex flex-column">
                        {/* TODO: Dodata indent i value (prvo nac jedincu koja to ima) */}
                        {beachData.characteristics?.length > 0 && beachData.characteristics.map((itemCharacteristic, indexCharacteristic) => (
                            <div
                                key={indexCharacteristic}
                                className="facility-unit__location-beaches-content-info-item d-flex justify-content-between align-items-center"
                            >
                                <p>{itemCharacteristic.title}</p>
                                {itemCharacteristic.isAvailable ? (
                                    <MdCheck className="content-info-item__available" />
                                ) : (
                                    <MdClose className="content-info-item__not-available" />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const renderPoliciesSectionView = (policiesData, section) => {
        if (!policiesData) return null;

        return (
            <div className="facility-unit__policies-content d-flex flex-column">
                <div className="facility-unit__policies-content-title-wrap d-flex flex-column">
                    <h3 className="facility-unit__policies-content-title">
                        {policiesData.title}
                    </h3>
                    {policiesData.description && (
                        <p className="facility-unit__policies-content-description">{policiesData.description}</p>
                    )}
                </div>
                {policiesData.items?.length > 0 && (
                    <div className="facility-unit__policies-content-items d-flex flex-column">
                        {policiesData.items.map((itemPoliciesData, indexPoliciesData) => (
                            <React.Fragment key={indexPoliciesData}>
                                <div className="facility-unit__policies-content-item-wrap d-flex flex-column">
                                    <div className="facility-unit__policies-content-item d-flex justify-content-between">
                                        <p className={`${itemPoliciesData.isChildItem && (section && section === 'otherPolicies') ? 'facility-unit__policies-content-item-title--otherPolicies' : 'facility-unit__policies-content-item-title'} d-flex align-items-center`}>
                                            <span>{itemPoliciesData.title}</span>
                                        </p>
                                        {itemPoliciesData.price && (
                                            <p className="facility-unit__policies-content-item-price-box d-flex flex-column align-items-center">
                                                <span className="facility-unit__policies-content-item-price-box-amount">
                                                    {itemPoliciesData.price.amountInEUR}
                                                </span>
                                                <span className="facility-unit__policies-content-item-price-box-unit">
                                                    {itemPoliciesData.price.priceUnit}
                                                </span>
                                            </p>
                                        )}
                                    </div>
                                </div>
                                {itemPoliciesData.description && (
                                    <div className="facility-unit__policies-content-item-wrap d-flex flex-column">
                                        <p className={`facility-unit__policies-content-item ${itemPoliciesData.isChildItem && (section && section === 'mandatoryAdditionalCosts') ? 'facility-unit__policies-content-item-description--mandatoryAdditionalCosts' : 'facility-unit__policies-content-item-description'} `}>
                                            {itemPoliciesData.description}
                                        </p>
                                    </div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const renderPriceListAndAvailabilityInfoView = (infoData) => {
        if (!infoData) return null;

        return (
            <div className="facility-unit__pricelist-info-box d-flex flex-column">
                <div className="d-flex justify-content-between flex-column flex-md-row align-items-start align-items-md-center">
                    <h3 className="facility-unit__pricelist-info-title">
                        {infoData.title}
                    </h3>
                    {infoData.description && (
                        <span className="facility-unit__pricelist-info-title-description">
                            {infoData.description}
                        </span>
                    )}
                </div>
                {infoData.discountItems?.length > 0 && (
                    <div className="facility-unit__pricelist-info-items d-flex flex-column">
                        {infoData.discountItems.map((itemPricelistDiscount, indexPricelistDiscount) => (
                            <div
                                key={indexPricelistDiscount}
                                className="facility-unit__pricelist-info-item d-flex flex-column flex-md-row align-items-start align-items-md-center"
                            >
                                <div className="facility-unit__pricelist-info-item-badge d-flex align-items-center">
                                    {itemPricelistDiscount.icon && (
                                        <img
                                            loading="lazy"
                                            src={itemPricelistDiscount.icon.sourceURL}
                                            alt={itemPricelistDiscount.icon.altText}
                                            title={itemPricelistDiscount.icon.titleText}
                                            className="facility-unit__pricelist-info-item-badge__icon"
                                            width={20}
                                            height={20}
                                        />
                                    )}
                                    <div className="facility-unit__pricelist-info-item-badge-title">
                                        {itemPricelistDiscount.title}
                                    </div>
                                </div>
                                <p className="facility-unit__pricelist-info-item-txt">
                                    {itemPricelistDiscount.percentage && (
                                        <span className="facility-unit__pricelist-info-item-txt-bold">
                                            {itemPricelistDiscount.percentage}&nbsp;
                                        </span>
                                    )}
                                    {itemPricelistDiscount.description && (
                                        <span
                                            dangerouslySetInnerHTML={{ __html: itemPricelistDiscount.description }}
                                        />
                                    )}
                                </p>
                            </div>
                        ))}
                    </div>
                )}
                {infoData.items?.length > 0 && (
                    <div className="facility-unit__pricelist-info-items d-flex flex-column">
                        {infoData.items.map((itemPricelistDiscount, indexPricelistDiscount) => (
                            <div
                                key={indexPricelistDiscount}
                                className="facility-unit__pricelist-info-item d-flex flex-column flex-md-row align-items-start align-items-md-center"
                            >
                                <div className="facility-unit__pricelist-info-item-badge d-flex align-items-center">
                                    {itemPricelistDiscount.icon && (
                                        <img
                                            loading="lazy"
                                            src={itemPricelistDiscount.icon.sourceURL}
                                            alt={itemPricelistDiscount.icon.altText}
                                            title={itemPricelistDiscount.icon.titleText}
                                            className="facility-unit__pricelist-info-item-badge__icon"
                                            width={20}
                                            height={20}
                                        />
                                    )}
                                    <div className="facility-unit__pricelist-info-item-badge-title">
                                        {itemPricelistDiscount.title}
                                    </div>
                                </div>
                                {(itemPricelistDiscount.amountInEUR || itemPricelistDiscount.priceUnit) && (
                                    <p className="facility-unit__pricelist-info-item-txt">
                                        <span className="facility-unit__pricelist-info-item-txt-bold">
                                            {itemPricelistDiscount.amountInEUR}&nbsp;
                                        </span>
                                        <span>{itemPricelistDiscount.priceUnit}</span>
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                {infoData.lastMinute && (
                    <div className="facility-unit__pricelist-info-items d-flex flex-column">
                        {infoData.lastMinute.items?.length > 0 && infoData.lastMinute.items.map((itemLastMinute, indexLastMinute) => (
                            <div
                                key={indexLastMinute}
                                className="facility-unit__pricelist-info-item d-flex flex-column flex-md-row align-items-start align-items-md-center"
                            >
                                {infoData.lastMinute.itemSaveText && (
                                    <div className="facility-unit__pricelist-info-item-badge d-flex align-items-center">
                                        <span>&#128293;</span>
                                        <p className="facility-unit__pricelist-info-item-badge-title">
                                            <span>{infoData.lastMinute.itemSaveText}</span>
                                        </p>
                                        <p className="facility-unit__pricelist-info-item-badge-title">
                                            {/* TODO: BACKEND - Ovo ce se minjati, bit ce iznos a ne postotak. */}
                                            <span>{itemLastMinute.discountPrecentage}</span>
                                        </p>
                                    </div>
                                )}
                                {infoData.lastMinute.itemDescription && (
                                    <p className="facility-unit__pricelist-info-item-txt">
                                        <span>{infoData.lastMinute.itemDescription}</span>
                                    </p>
                                )}
                                {(itemLastMinute.from || itemLastMinute.to) && (
                                    <button
                                        className={`facility-unit__pricelist-info-item-txt facility-unit__pricelist-info-item-suggested-btn ${isLoadingUnitPriceAvailabilityCalendar ? 'disableCurrentElementContent--transparent' : ' '}`}
                                        // TODO: #58308 Privremeno, nakon comitt-a prve verzije će se uklonit ili doradit.
                                        // Potreban mi je scroll na misec za koji se odaberu datumi.
                                        onClick={() => handleSelectSuggestedDates(itemLastMinute.from_standardized, itemLastMinute.to_standardized)}
                                        type="button"
                                    >
                                        <span className="facility-unit__pricelist-info-item-txt-date">
                                            {itemLastMinute.from}
                                            &nbsp;&minus;&nbsp;
                                            {itemLastMinute.to}
                                        </span>
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const renderPriceListAndAvailabilityRainyDayRefundView = (infoData) => {
        if (!infoData) return null;

        return (
            <div className="facility-unit__pricelist-info-box d-flex flex-column">
                <div className="facility-unit__pricelist-info-items d-flex flex-column">
                    <div
                        className="facility-unit__pricelist-info-item d-flex flex-column flex-md-row align-items-start align-items-md-center"
                    >
                        <div className="facility-unit__pricelist-info-item-badge d-flex align-items-center">
                            <div className="facility-unit__pricelist-info-item-badge-title">
                                {infoData.gratisText}
                            </div>
                        </div>
                        {infoData.hyperlink && (
                            <a
                                className="facility-unit__pricelist-info-item-txt"
                                href={infoData.hyperlink.redirectURL}
                                target="_blank"
                                rel="noopener noreferrer"
                                // TODO: #58308 BACKEND - Pitati za sta služi i di triba bit ovaj "seoTitle".
                                title={infoData.hyperlink.seoTitle}
                            >
                                {infoData.hyperlink.redirectURLTitle}
                            </a>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderPriceListAndAvailabilityIncludedInPriceView = (infoData) => {
        if (!infoData) return null;
        const lastDescription = infoData.items && [...infoData.items].reverse().find((item) => item.description)?.description;

        return (
            <div className="facility-unit__pricelist-info-box d-flex flex-column">
                <h3 className="facility-unit__pricelist-info-title">
                    {infoData.title}
                </h3>
                {infoData.items?.length > 0 && (
                    <div className="facility-unit__pricelist-info-included-items d-flex flex-column">
                        {infoData.items.map((itemIncludedInPrice, indexIncludedInPrice) => (
                            <div
                                key={indexIncludedInPrice}
                                className="facility-unit__pricelist-info__included__item d-flex justify-content-between align-items-center"
                            >
                                <p className="facility-unit__pricelist-info-item-txt">
                                    {itemIncludedInPrice.title}
                                    {itemIncludedInPrice.description && (
                                        <span>*</span>
                                    )}
                                </p>
                            </div>
                        ))}
                    </div>
                )}
                {lastDescription && (
                    <p className="facility-unit__pricelist-info-item-description">
                        <span>*</span>{lastDescription}
                    </p>
                )}
            </div>
        );
    };

    const renderPriceListAndAvailabilityShortStayExtraChargeView = (infoData) => {
        if (!infoData) return null;

        return (
            <div className="facility-unit__pricelist-info-footer d-flex align-items-center">
                <MdOutlineInfo className="facility-unit__pricelist-info-footer-icon" />
                <p
                    className="facility-unit__pricelist-info-footer-txt"
                    // TODO: #58308 DIZAJN - Pitat dizajnera kako prikazati ove "tooltips"(array).
                    title={infoData.tooltips?.join(' | ')}
                >
                    {infoData.description}
                </p>
            </div>
        );
    };

    const renderPriceListAndAvailabilityTouristTaxInfoView = (infoData) => {
        if (!infoData) return null;

        return (
            <div className="facility-unit__pricelist-info-footer d-flex align-items-center">
                <MdOutlineInfo className="facility-unit__pricelist-info-footer-icon" />
                <p className="facility-unit__pricelist-info-footer-txt">
                    {infoData}
                </p>
            </div>
        );
    };

    const renderGuestsDisplayText = () => {
        const parts = formatGuestsDisplayText({
            numberOfPersons: totalGuestsReservationParameters,
            numberOfAdults: totalAdultsReservationParameters,
            numberOfChildren: totalChildrenReservationParameters,
            withChildrenTemplate: presentationUnit.numberOfGuestsForm?.selectedAdultsWithKidsTemplate,
            forceChildrenTemplate: true,
            splitText: true,
        });
        return (
            <p className="facility-unit__reservation-box-form-dates-info-txt-bold d-flex align-items-center">
                <span className="facility-unit__reservation-box-form-guests-content-description">
                    {parts[0]}&nbsp;{parts[1]}
                </span>
            </p>
        );
    };

    const renderRobinsonDescription = () => {
        const robinsonDescription = presentationUnit.robinsonOverview.description;
        if (expandedRobinson) return robinsonDescription;
        if (robinsonDescription.length > maxLengthForInitialRobinsonDescription) {
            return `${robinsonDescription.substring(0, maxLengthForInitialRobinsonDescription)}...`;
        }
        return robinsonDescription;
    };

    const renderFinalSelectedUnitsView = (finalSelectedUnits) => (
        <div className="facility-unit__reservation-box-form-footer-unit-titles">
            {finalSelectedUnits?.map((itemUnitPriceCalculation, indexUnitPriceCalculation) => (
                <div
                    key={indexUnitPriceCalculation}
                    className={`facility-unit__reservation-box-form-footer-unit ${!itemUnitPriceCalculation?.availableForBooking ? 'facility-unit__reservation-box-form-footer-unit--unavailable' : ' '} d-flex justify-content-between align-items-center`}
                >
                    <div className="d-flex align-items-center">
                        {/* TODO: #58308 Dizajner treba odlučiti di ce ovo biti. */}
                        {presentationUnit?.units?.unitsShortInfo?.length > 1 && (
                            <button
                                className="facility-unit__reservation-box-form-footer-unit-remove"
                                type="button"
                                aria-label="Remove unit from reservation"
                                onClick={() => {
                                    removeUnitFromSelectedUnitsInReservationarameters(unitPriceCalculationUnitsById[itemUnitPriceCalculation.unitId]);
                                    if (!IS_FRAME_SERVICE) {
                                        dispatchTrackingEvent(RESERVATION_FORM_REMOVE_UNIT);
                                        if (mainUnitID === itemUnitPriceCalculation.unitId) {
                                            dispatchTrackingEvent(RESERVATION_FORM_REMOVE_MAIN_UNIT);
                                        }
                                    }
                                }}
                            >
                                <MdClose className="facility-unit__reservation-box-form-footer-unit-remove-icon" />
                            </button>
                        )}
                        <div className="facility-unit__reservation-box-form-footer-unit-title__info">
                            <p className="facility-unit__reservation-box-form-footer-unit-title">
                                {itemUnitPriceCalculation.unitTitle}
                            </p>
                            <p className="facility-unit__reservation-box-form-footer-unit-txt">
                                {/* TODO: #58308 Ovo je logika iz prve verzije, uklonit ako ne treba. */}
                                {/* {itemUnitPriceCalculation.discountedPrice ? (
                                    <span>{itemUnitPriceCalculation.discountedPrice} x </span>
                                ) : (
                                    <span>{itemUnitPriceCalculation.originalPrice} x </span>
                                )} */}
                                {unitPriceCalculation.simpleReservationFormCalculatedInformation.numberOfNights && (
                                    <span>
                                        {presentationUnit.simpleReservationFormLabels.nuberOfNightsTitle}:&nbsp;
                                        {unitPriceCalculation.simpleReservationFormCalculatedInformation.numberOfNights}
                                    </span>
                                )}
                            </p>
                        </div>
                    </div>
                    <div
                        className="facility-unit__reservation-box-form-footer-unit-guests d-flex align-items-center"
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                            openGuestSelectModal(unitPriceCalculationUnitsById[itemUnitPriceCalculation.unitId]);
                            if (!IS_FRAME_SERVICE) dispatchTrackingEvent(RESERVATION_FORM_PERSONS_EDIT);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') openGuestSelectModal(unitPriceCalculationUnitsById[itemUnitPriceCalculation.unitId]);
                        }}
                    >
                        <div>
                            <p className="facility-unit__reservation-box-form-footer-unit-title facility-unit__reservation-box-form-footer-unit-title--left">
                                {presentationUnit.simpleReservationFormLabels.guestsTitle}:&nbsp;{sumAllUnitGuests(itemUnitPriceCalculation)}
                            </p>
                            <p className="facility-unit__reservation-box-form-footer-unit-txt">
                                <span>{presentationUnit.simpleReservationFormLabels.adultsTitle}:&nbsp;{itemUnitPriceCalculation.adultsCount}</span>
                                {/* Logika sa 'visible' : 'invisible' je postavljena da bi sirina bila ujednačena ako jedna od jedinica ima odabranu djecu. */}
                                {sumAllReservationChildrens > 0 && (
                                    <span className={sumAllUnitChildren(itemUnitPriceCalculation) > 0 ? 'visible' : 'invisible'}>
                                        &#44;&nbsp;{presentationUnit.simpleReservationFormLabels.childrenTitle}:&nbsp;{sumAllUnitChildren(itemUnitPriceCalculation)}
                                    </span>
                                )}
                            </p>
                        </div>
                        <div className="facility-unit__reservation-box-form-footer-unit-guests-icon-wrap d-flex justify-content-center align-items-center">
                            <MdOutlineCreate className="facility-unit__reservation-box-form-footer-unit-guests-icon" />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    const isAIFrameService = isClientSideRendering && new URLSearchParams(window.location.search).get('adriaticAI') === '1';
    const baseUrl = unitPriceCalculation?.simpleReservationFormCalculatedInformation?.createReservationButtonURL;

    const finalUrl = (() => {
        if (!baseUrl) return null;
        if (!isAIFrameService) return baseUrl;

        const url = new URL(baseUrl, window.location.origin);
        url.searchParams.set('utm_source', 'adriaticaisearch');
        url.searchParams.set('utm_medium', 'internal');
        url.searchParams.set('utm_campaign', 'ai_search');
        url.searchParams.set('adriaticAI', '1');

        return url.toString();
    })();

    const renderReservation = () => (
        <div className="facility-unit__reservation__mob facility-unit__reservation-mob--cart d-flex d-lg-none flex-column">
            <button
                className={`facility-unit__reservation__mob__top-btn ${isLoadingUnitPriceCalculation ? 'disableCurrentElementContent' : ' '} d-flex flex-column justify-content-center align-items-center`}
                type="button"
                onClick={handleOpenReservationMobileModal}
                aria-label="Open reservation modal"
            >
                <span className="facility-unit__reservation__mob__line" />
                <span className={`facility-unit__reservation__mob__error-wrapper ${unitPriceCalculation?.simpleReservationFormCalculatedInformation?.reservationFormError ? 'facility-unit__reservation__mob__error-wrapper__show' : ''}`}>
                    <span className="facility-unit__reservation__mob__error">
                        {unitPriceCalculation?.simpleReservationFormCalculatedInformation?.reservationFormError}
                    </span>
                </span>
                {unitPriceCalculation?.simpleReservationFormCalculatedInformation?.unalocatedPersonsWarning && (
                    <span
                        className="facility-unit__reservation-box-warning-box d-flex justify-content-center align-items-center"
                        role="button"
                        tabIndex={0}
                        onClick={scrollIntoUnitsShortInfoView}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') scrollIntoUnitsShortInfoView();
                        }}
                    >
                        <span className="facility-unit__reservation-box-warning-box-icon d-flex">
                            <MdInfoOutline className="reservation-box-warning-box-icon" />
                        </span>
                        <span
                            className="facility-unit__reservation-box-warning-box-txt"
                        >
                            {unitPriceCalculation.simpleReservationFormCalculatedInformation.unalocatedPersonsWarning}
                        </span>
                    </span>
                )}
                {unitPriceCalculation?.simpleReservationFormCalculatedInformation?.continueWithoutMainUnitText && (
                    <a
                        href={unitPriceCalculation.simpleReservationFormCalculatedInformation.continueWithoutMainUnitURL}
                        className="facility-unit__reservation-box-warning-box d-flex justify-content-center align-items-center"
                        style={{ textDecoration: 'none', color: 'inherit' }}
                        target={`${isMobileClient ? '_self' : '_blank'}`}
                        rel={isMobileClient ? 'noreferrer' : 'noopener noreferrer'}
                        tabIndex={0}
                        role="button"
                    >
                        <div className="facility-unit__reservation-box-warning-box-icon d-flex">
                            <MdInfoOutline className="reservation-box-warning-box-icon" />
                        </div>
                        <p
                            className="facility-unit__reservation-box-warning-box-txt"
                        >
                            {unitPriceCalculation.simpleReservationFormCalculatedInformation.continueWithoutMainUnitText}
                        </p>
                    </a>
                )}
            </button>
            <div className={`facility-unit__reservation__mob__info-wrap ${isLoadingUnitPriceCalculation ? 'disableCurrentElementContent--spinner' : ' '} d-flex align-items-end`}>
                {unitPriceCalculation?.simpleReservationFormCalculatedInformation?.totalPriceValueInEUR && (
                    <div className={`facility-unit__reservation__mob__info d-flex justify-content-between align-items-center ${isLoadingUnitPriceCalculation ? 'disableCurrentElementContent--spinner' : ' '}`}>
                        <div className="facility-unit__reservation__mob__info__price">
                            <button
                                className="facility-unit__reservation__mob__info__main d-flex flex-column align-items-start"
                                type="button"
                                onClick={handleOpenReservationMobileModal}
                                aria-label="Open reservation modal"
                            >
                                {unitPriceCalculation?.simpleReservationFormCalculatedInformation?.totalPriceValueInEURWithoutDiscounts && (
                                    <span>
                                        <span className="facility-unit__reservation__mob__info__main__price-no-discount">
                                            {unitPriceCalculation.simpleReservationFormCalculatedInformation.totalPriceValueInEURWithoutDiscounts}
                                        </span>
                                        {unitPriceCalculation?.simpleReservationFormCalculatedInformation?.savingsPercent && (
                                            <span className="facility-unit__reservation__mob__info__main__discount-percent">
                                                {unitPriceCalculation.simpleReservationFormCalculatedInformation.savingsPercent}
                                            </span>
                                        )}
                                    </span>
                                )}
                                <span className="facility-unit__reservation__mob__info__main__price">
                                    {unitPriceCalculation?.simpleReservationFormCalculatedInformation?.totalPriceValueInEUR}
                                </span>
                                {presentationUnit.simpleReservationFormLabels?.showReservationDetails && (
                                    <span className="facility-unit__reservation__mob__info__main__details-wrap">
                                        <span className="facility-unit__reservation__mob__info__main__details">
                                            {presentationUnit.simpleReservationFormLabels.showReservationDetails}
                                        </span>
                                        <MdOpenInNew className="facility-unit__reservation__mob__info__main__icon" />
                                    </span>
                                )}
                            </button>
                        </div>
                    </div>
                )}
                <div className={unitPriceCalculation?.simpleReservationFormCalculatedInformation?.totalPriceValueInEUR ? 'facility-unit__reservation__mob__reservation-btn-wrap' : 'reservation-btn-wrap-full'}>
                    {unitPriceCalculation?.simpleReservationFormCalculatedInformation?.createReservationButtonURL ? (
                        <a
                            className={`adr-btn-primary-md ${isLoadingUnitPriceCalculation ? 'disableCurrentElementContent--transparent' : ''}`}
                            href={finalUrl}
                            onClick={() => {
                                if (!IS_FRAME_SERVICE) dispatchTrackingEvent(RESERVATION_FORM_BOOK_NOW_CTA_CLICK, { unitID: mainUnitID });
                            }}
                        >
                            <span
                                className="facility-unit__reservation__mob__reservation-btn__txt"
                            >
                                {presentationUnit.simpleReservationFormLabels.reserveTitle}
                            </span>
                        </a>
                    ) : (
                        <button
                            className={`adr-btn-primary-md w-100 ${isLoadingUnitPriceCalculation ? 'disableCurrentElementContent--transparent' : ''}`}
                            type="button"
                            onClick={() => {
                                handleClickStartReservation();
                                handleTrackerReservationTextCTA();
                            }}
                        >
                            <span
                                className="facility-unit__reservation__mob__reservation-btn__txt"
                            >
                                {reservationButtonTextCTA || presentationUnit.simpleReservationFormLabels.checkAvailabilityTitle}
                            </span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );

    const renderFacilityUnitCardSkeleton = () => (
        !unitPriceCalculation && isLoadingUnitPriceCalculation && (
            Array.from({
                length: Math.min(presentationUnit.units.unitsShortInfo?.length || 0, initialVisibleUnitCards),
            }).map((_, index) => (
                <div key={index} className="facility-unit-card facility-unit-card--skeleton d-flex justify-content-between">
                    <div className="facility-unit-card__slider" />
                    <div className="facility-unit-card__content d-flex flex-column justify-content-between">
                        <div className="facility-unit-card__content-header d-flex flex-column">
                            <h2 className="facility-unit-card__content-header-title">
                                <Skeleton width="65%" height={26} />
                            </h2>
                            <div className="facility-unit-card__content-header-info-wrap d-flex flex-column">
                                <div className="facility-unit-card__content-header-basic-info d-flex flex-wrap">
                                    <Skeleton width={90} height={18} />
                                    <Skeleton width={110} height={18} />
                                    <Skeleton width={75} height={18} />
                                </div>
                            </div>
                        </div>
                        <div className="facility-unit-card__content-more d-flex align-items-center toggler__content--visible">
                            <Skeleton circle={true} width={20} height={20} />
                            <Skeleton width={95} height={18} />
                        </div>
                        <ul className="facility-unit-card__content-footer d-flex flex-wrap">
                            {[1, 2].map((item) => (
                                <li
                                    key={item}
                                    className="facility-unit-card__content-footer-item d-flex align-items-center"
                                >
                                    <Skeleton circle={true} width={18} height={18} />
                                    <Skeleton width={80} height={16} />
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="facility-unit-card__info d-flex flex-column justify-content-between">
                        <div className="facility-unit-card__info-price-responsive-wrap">
                            <div className="facility-unit-card__info-price d-flex flex-column mb-2">
                                <Skeleton width={120} height={30} />
                            </div>
                            <div className="facility-unit-card__info-picker d-flex justify-content-between align-items-center toggler__content--visible">
                                <div className="facility-unit-card__info-picker__txt d-flex flex-column">
                                    <Skeleton width={110} height={18} />
                                    <Skeleton width={145} height={16} />
                                </div>
                                <Skeleton circle={true} width={36} height={36} />
                            </div>
                        </div>
                        <div className="facility-unit-card__info-toggler">
                            <div className="facility-unit-card__info-toggler-switch-wrapper d-flex justify-content-end align-items-center">
                                <Skeleton width={95} height={18} />
                            </div>
                        </div>
                    </div>
                </div>
            ))
        )
    );

    const renderFinalSelectedUnitsSkeletonView = () => (
        <div className="facility-unit__reservation-box-form-footer-unit-titles">
            <div className="facility-unit__reservation-box-form-footer-unit facility-unit__reservation-box-form-footer-unit--skeleton d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                    {presentationUnit?.units?.unitsShortInfo?.length > 1 && (
                        <Skeleton
                            width={20}
                            height={20}
                            className="facility-unit__reservation-box-form-footer-unit-remove"
                        />
                    )}
                    <div className="facility-unit__reservation-box-form-footer-unit-title__info">
                        <p className="facility-unit__reservation-box-form-footer-unit-title">
                            <Skeleton width={130} height={18} />
                        </p>

                        <p className="facility-unit__reservation-box-form-footer-unit-txt">
                            <Skeleton width={85} height={14} />
                        </p>
                    </div>
                </div>
                <div className="facility-unit__reservation-box-form-footer-unit-guests d-flex align-items-center">
                    <div>
                        <p className="facility-unit__reservation-box-form-footer-unit-title facility-unit__reservation-box-form-footer-unit-title--left">
                            <Skeleton width={90} height={18} />
                        </p>

                        <p className="facility-unit__reservation-box-form-footer-unit-txt">
                            <Skeleton width={120} height={14} />
                        </p>
                    </div>
                    <div className="facility-unit__reservation-box-form-footer-unit-guests-icon-wrap d-flex justify-content-center align-items-center">
                        <Skeleton circle={true} width={20} height={20} />
                    </div>
                </div>
            </div>
        </div>
    );

    const ExpandDescriptionButtonIcon = isDescriptionExpanded ? MdArrowDropUp : MdArrowDropDown;

    return (
        <>
            {/* TODO: #58308 Privremeno, potrebno doraditi skeleton. */}
            {isLoadingInitialRequests && (!temporaryPublicResourcesSSR && !presentationUnitSSR) && <FacilitySkeleton />}
            <BreadCrumbs
                breadcrumbs={presentationUnit?.breadcrumbs}
                breadcrumbsStructuredData={presentationUnit?.breadcrumbsStructuredData}
                isLoading={isLoadingInitialRequests && !temporaryPublicResourcesSSR && !presentationUnitSSR}
            />
            {fetchError && <AxiosError />}
            {presentationUnit && (
                <section className="row">
                    <article className="col-12">
                        {/* Unit overview */}
                        <div className="facility-unit__overview">
                            <div className="facility-unit__overview-header d-flex flex-column">
                                <div className="facility-unit__overview-header-wrap">
                                    <h1 className="facility-unit__overview-header-title">{presentationUnit.title}</h1>
                                    <div className="facility-unit__overview-header-icons d-flex">
                                        {/* Duplicirana komponenta jer je dizajn zahtjevao tako, na mobilnom je button pozicioniran u odnosu na gallery-u, dok je na desktopu unutar header-a sa naslovom. */}
                                        {!IS_FRAME_SERVICE && (
                                            <WishlistWidget
                                                className="d-none d-md-flex"
                                                unitsData={[mainUnitDataForWishlistWidget]}
                                                initialWishlistedUnits={initialWishlistedUnits}
                                                modalData={presentationUnit.wishlistRemoveConfirmationModal}
                                                isUserLoggedIn={Boolean(currentUser?.id)}
                                                user={currentUser}
                                                openLoginModal={() => setIsLoginModalOpen(true)}
                                            />
                                        )}
                                        {(!isMobileClient || !isMounted) && presentationUnit.socialShare && (
                                            <div className="d-none d-md-block">
                                                <SocialShare
                                                    customButton={(
                                                        <span className="facility-unit__overview-header-icon-wrap d-flex justify-content-center align-items-center">
                                                            <MdOutlineShare className="facility-unit__overview-header__icon" size={20} />
                                                        </span>
                                                    )}
                                                    shareURL={shareURL}
                                                    socialShare={presentationUnit.socialShare}
                                                    className="socialShare"
                                                    trackingEvents={{
                                                        onOpenModal: 'unitPresentationSocialShareOpen',
                                                        onSocialShareSelect: 'unitPresentationSocialShareSelect',
                                                        id: mainUnitID,
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {(presentationUnit.averageAccommodationReviewsShortInfo || presentationUnit.distanceFromTheBeachShortInfo) && (
                                    <div className="facility-unit__overview-header-info d-flex align-items-start align-items-sm-center flex-column flex-sm-row flex-wrap">
                                        {presentationUnit.averageAccommodationReviewsShortInfo && (
                                            <button
                                                type="button"
                                                className="facility-unit__overview-header-info-review facility-unit__overview-header-dot d-flex align-items-center"
                                                onClick={() => handleReviewsModal(UNIT_PRESENTATION_REVIEWS_FROM_GRADE_OPEN)}
                                            >
                                                <MdOutlineStar
                                                    className="facility-unit__overview-header-info-review-icon"
                                                    size={24}
                                                />
                                                <span className="facility-unit__overview-header-info-review-title">
                                                    {presentationUnit.averageAccommodationReviewsShortInfo.title}
                                                    &nbsp;
                                                    {presentationUnit.averageAccommodationReviewsShortInfo.averageGrade}
                                                    &nbsp;
                                                    <span className="facility-unit__overview-header-info-review-info">
                                                        ({presentationUnit.averageAccommodationReviewsShortInfo.reviewsCount})
                                                    </span>
                                                </span>
                                            </button>
                                        )}
                                        {presentationUnit.distanceFromTheBeachShortInfo && (
                                            <p className="facility-unit__overview-header-info-distance facility-unit__overview-header-dot d-flex align-items-center">
                                                {presentationUnit.distanceFromTheBeachShortInfo.icon && (
                                                    <>
                                                        <span
                                                            className={presentationUnit.distanceFromTheBeachShortInfo.icon.sourceURL}
                                                            aria-hidden="true"
                                                        />
                                                        <span className="sr-only">{presentationUnit.distanceFromTheBeachShortInfo.icon.altText}</span>
                                                    </>
                                                )}
                                                <span className="facility-unit__overview-header-info-distance-title facility-unit__overview-header-info-distance-title--bold">
                                                    {presentationUnit.distanceFromTheBeachShortInfo.distanceInMeters}
                                                </span>
                                                <span className="facility-unit__overview-header-info-distance-title">
                                                    {presentationUnit.distanceFromTheBeachShortInfo.title}
                                                </span>
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                            {/* Header gallery */}
                            {presentationUnit.headerGallery && (
                                <div
                                    className="facility-unit__overview-mozaik"
                                    id={presentationUnit.headerGallery.sectionId}
                                >
                                    {presentationUnit.headerGallery.showAllGalleryItemsTitle && (
                                        <button
                                            type="button"
                                            className="facility-unit__overview-mozaik-btn-gallery d-flex justify-content-center justify-content-md-between align-items-center"
                                            onClick={handleUnitMainGalleryModal}
                                            aria-label="Show all gallery images"
                                        >
                                            <MdFilter className="facility-unit__overview-mozaik-btn-gallery-icon overview-mozaik-btn-gallery-icon--filter d-none d-md-block" />
                                            <MdOutlineGridView className="facility-unit__overview-mozaik-btn-gallery-icon overview-mozaik-btn-gallery-icon--grid d-block d-md-none" />
                                            <span className="facility-unit__overview-mozaik-btn-gallery-title d-none d-md-block">
                                                {presentationUnit.headerGallery.showAllGalleryItemsTitle}
                                            </span>
                                        </button>
                                    )}
                                    {/* Mobile gallery overview */}
                                    <div className={`${isLoadingUnitMainGallery ? 'disableCurrentElementContent--spinner' : ' '} facility-unit__overview-mobile d-block d-md-none`}>
                                        <div
                                            id="unitGalleryCarousel"
                                            className="carousel slide h-100"
                                            data-ride="carousel"
                                            data-interval="false"
                                        >
                                            <div
                                                ref={unitGalleryCarouselInnerRef}
                                                className="carousel-inner h-100"
                                                role="button"
                                                tabIndex={0}
                                                onClick={handleMobileClickUnitMainGallery}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') handleMobileClickUnitMainGallery();
                                                }}
                                                onTouchStart={handleTouchStart}
                                                onTouchEnd={handleTouchEnd}
                                            >
                                                {!unitMainGallery ? (
                                                    <div className="facility-unit__overview-mobile__slide carousel-item active h-100">
                                                        <img
                                                            loading="eager"
                                                            fetchpriority="high"
                                                            src={presentationUnit.headerGallery.galleryItems.mainImage.sourceURL}
                                                            alt={presentationUnit.headerGallery.galleryItems.mainImage.altText}
                                                            width={1275}
                                                            height={850}
                                                            className="facility-unit__overview-mobile-img"
                                                        />
                                                    </div>
                                                ) : (
                                                    unitMainGallerySlides.map((slide, index) => (
                                                        <div
                                                            key={index}
                                                            className={`facility-unit__overview-mobile__slide carousel-item ${index === currentSlideIndex ? 'active' : ''}`}
                                                        >
                                                            <img
                                                                src={slide.img.image.sourceURL}
                                                                alt={slide.img.image.altText}
                                                                title={slide.img.image.titleText}
                                                                className="facility-unit__overview-mobile-img"
                                                                width={1275}
                                                                height={850}
                                                            />
                                                            {slide.additionalDataCreatedOnFront && (
                                                                <div className="facility-unit__overview-mobile-track d-flex align-items-center">
                                                                    <p className="facility-unit__overview-mobile-track-title">
                                                                        {slide.additionalDataCreatedOnFront.title}
                                                                    </p>
                                                                    {slide.additionalDataCreatedOnFront.totalAreaSquareMeters && (
                                                                        <p className="facility-unit__overview-mobile-track-info d-flex align-items-center">
                                                                            <MdOutlineStraighten className="facility-unit__overview-mobile-track-info-icon" />
                                                                            <span
                                                                                className="facility-unit__overview-mobile-track-info-txt"
                                                                                dangerouslySetInnerHTML={{ __html: slide.additionalDataCreatedOnFront.totalAreaSquareMeters }}
                                                                            />
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                            <a
                                                ref={unitGalleryCarouselPrevBtn}
                                                className="carousel-control-prev"
                                                href="#unitGalleryCarousel"
                                                role="button"
                                                data-slide="prev"
                                                onClick={() => {
                                                    handleUnitGalleryCarouselClick(unitMainGalleryLeftArrow);
                                                }}
                                            >
                                                <span
                                                    className="facility-unit__overview-mobile-controls"
                                                    aria-hidden="true"
                                                >
                                                    <MdArrowLeft className="facility-unit__overview-mobile-controls-icon" />
                                                </span>
                                                <span className="sr-only">Previous</span>
                                            </a>
                                            <a
                                                ref={unitGalleryCarouselNextBtn}
                                                className="carousel-control-next"
                                                href="#unitGalleryCarousel"
                                                role="button"
                                                data-slide="next"
                                                onClick={() => {
                                                    handleUnitGalleryCarouselClick(unitMainGalleryRightArrow);
                                                }}
                                            >
                                                <span
                                                    className="facility-unit__overview-mobile-controls"
                                                    aria-hidden="true"
                                                >
                                                    <MdArrowRight className="facility-unit__overview-mobile-controls-icon" />
                                                </span>
                                                <span className="sr-only">Next</span>
                                            </a>
                                        </div>
                                    </div>
                                    {/* Desktop gallery overview */}
                                    <div
                                        className="facility-unit__overview-mozaik-desktop-gallery-wrapper"
                                        role="button"
                                        tabIndex={0}
                                        onClick={handleUnitMainGalleryModal}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                handleUnitMainGalleryModal();
                                            }
                                        }}
                                    >
                                        <div className="facility-unit__overview-mozaik__main facility-unit__overview-mozaik-image-wrapper">
                                            <img
                                                loading="eager"
                                                fetchpriority="high"
                                                src={presentationUnit.headerGallery.galleryItems.mainImage.sourceURL}
                                                alt={presentationUnit.headerGallery.galleryItems.mainImage.altText}
                                                title={presentationUnit.headerGallery.galleryItems.mainImage.titleText}
                                                className="facility-unit__overview-mozaik-image"
                                            />
                                        </div>
                                        {presentationUnit.headerGallery.galleryItems.additionalImages.length > 0 && (
                                            <div className="facility-unit__overview-mozaik-additional">
                                                {presentationUnit.headerGallery.galleryItems.additionalImages?.slice(0, 4).map((itemAdditionalImage, indexAdditionalImage) => (
                                                    <div key={indexAdditionalImage} className="facility-unit__overview-mozaik-image-wrapper">
                                                        <img
                                                            src={itemAdditionalImage.sourceURL}
                                                            alt={itemAdditionalImage.altText}
                                                            title={itemAdditionalImage.titleText}
                                                            className="facility-unit__overview-mozaik-image"
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {presentationUnit.highlightedFeatures?.length > 0 && (
                                            <div className="facility-unit__highlighted-features d-none d-md-flex align-items-center">
                                                {presentationUnit.highlightedFeatures.map((itemFeatured, indexFeatured) => (
                                                    <div
                                                        key={indexFeatured}
                                                        className="facility-unit__highlighted-features-item d-flex align-items-center"
                                                    >
                                                        {itemFeatured.icon && (
                                                            <img
                                                                src={itemFeatured.icon.sourceURL}
                                                                alt={itemFeatured.icon.altText}
                                                                title={itemFeatured.icon.titleText}
                                                                width={20}
                                                                height={20}
                                                            />
                                                        )}
                                                        <p className="facility-unit__highlighted-features-item-txt">
                                                            {itemFeatured.title}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    {/* Duplicirana komponenta jer je dizajn zahtjevao tako, na mobilnom je button pozicioniran u odnosu na gallery-u, dok je na desktopu unutar header-a sa naslovom. */}
                                    {!IS_FRAME_SERVICE && (
                                        <WishlistWidget
                                            className="d-md-none"
                                            unitsData={[mainUnitDataForWishlistWidget]}
                                            initialWishlistedUnits={initialWishlistedUnits}
                                            modalData={presentationUnit.wishlistRemoveConfirmationModal}
                                            isUserLoggedIn={Boolean(currentUser?.id)}
                                            user={currentUser}
                                            openLoginModal={() => setIsLoginModalOpen(true)}
                                        />
                                    )}
                                    {(isMobileClient || !isMounted) && presentationUnit.socialShare && (
                                        <SocialShare
                                            customButton={(
                                                <span className="facility-unit__overview-header-icon-wrap facility-unit__overview-header-icon-wrap--on-gallery d-flex justify-content-center align-items-center">
                                                    <MdOutlineShare className="facility-unit__overview-header__icon" size={20} />
                                                </span>
                                            )}
                                            customClassNameForButton="facility-unit__mobile-social-button d-md-none"
                                            shareURL={shareURL}
                                            socialShare={presentationUnit.socialShare}
                                            className="socialShare"
                                            trackingEvents={{
                                                onOpenModal: 'unitPresentationSocialShareOpen',
                                                onSocialShareSelect: 'unitPresentationSocialShareSelect',
                                                id: mainUnitID,
                                            }}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    </article>
                    {/* Left content 70% desktop, 100% mobile */}
                    <article className="col-lg-8 col-12">
                        {/* HORIZONTAL NAVIGATION */}
                        <div className="facility-unit__navigation">
                            <ul className="facility-unit__navigation-group d-flex">
                                {presentationUnit.menu?.map((itemMenu, indexMenu) => (
                                    <li key={indexMenu}>
                                        <button
                                            className={`facility-unit__navigation-btn ${activeSection === itemMenu.sectionId ? 'facility-unit__navigation-btn--active' : ''}`}
                                            type="button"
                                            onClick={() => scrollToSection(itemMenu.sectionId)}
                                        >
                                            {itemMenu.itemTitle}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        {/* Content Details */}
                        <div className="facility-unit__detail d-flex flex-column">
                            {/* Overview information */}
                            {presentationUnit.overviewInformation && (
                                <div
                                    className="facility-unit__detail-box facility-unit__description d-flex flex-column"
                                    // TODO: BACKEND - Provjerit sa backendom zasto nema id-a sekcije (potreban za scroll iz menu-a).
                                    // Provjereno sa backendom, id ce imati samo 4 sekcije, uklonit id sa elemenata koji nemaju ID.
                                    id={presentationUnit.overviewInformation.sectionId}
                                >
                                    <div className="facility-unit__description-header d-flex flex-column">
                                        <h2
                                            className="facility-unit__detail-title facility-unit__detail-title--upper speakable-content"
                                        >
                                            {presentationUnit.overviewInformation.title}
                                        </h2>
                                        <p
                                            className={`facility-unit__description-txt ${isDescriptionExpanded ? 'facility-unit__description-txt--expanded' : ''} ${presentationUnit.facilityDescription ? 'facility-unit__description-txt--short' : ''} speakable-content`}
                                            dangerouslySetInnerHTML={{ __html: presentationUnit.overviewInformation.unitDescriptionHTMLContent }}
                                        />
                                        {!presentationUnit.facilityDescription && (
                                            <>
                                                <button
                                                    type="button"
                                                    className="facility-unit__description-show-more-button"
                                                    onClick={() => {
                                                        setIsDescriptionExpanded((prev) => {
                                                            const next = !prev;
                                                            if (!IS_FRAME_SERVICE && next) {
                                                                dispatchTrackingEvent(UNIT_PRESENTATION_DESCRIPTION_SHOW_MORE);
                                                            }

                                                            return next;
                                                        });
                                                    }}
                                                >
                                                    <span className="facility-unit__description-show-more-text">{isDescriptionExpanded ? presentationUnit.showLessText : presentationUnit.showMoreText}</span>
                                                    <ExpandDescriptionButtonIcon className="description-show-more-text__icon" />
                                                </button>
                                                <ModalControl
                                                    id={FACILITY_UNIT_DESCRIPTION_MODAL_ID}
                                                    title={presentationUnit.overviewInformation.title}
                                                    isOpen={isDescriptionExpanded}
                                                    onClose={() => setIsDescriptionExpanded(false)}
                                                    animate={true}
                                                    disableBodyScroll={false}
                                                    hideOnDesktop={true}
                                                >
                                                    <ModalBody>
                                                        <p
                                                            className="facility-unit__description-txt-modal"
                                                            dangerouslySetInnerHTML={{ __html: presentationUnit.overviewInformation.unitDescriptionHTMLContent }}
                                                        />
                                                    </ModalBody>
                                                </ModalControl>
                                            </>
                                        )}
                                    </div>
                                    { renderReservation() }
                                    <div className="facility-unit__description-content d-flex flex-wrap">
                                        <div className="facility-unit__description-popular">
                                            {presentationUnit.overviewInformation.unitBasicInformations?.length > 0 && (
                                                <div className="facility-unit__description-boxes d-flex">
                                                    {presentationUnit.overviewInformation.unitBasicInformations.map((itemUnitBasicInformation, indexUnitBasicInformation) => (
                                                        <div
                                                            className="facility-unit__description-box d-flex flex-column justify-content-center align-items-center flex-wrap"
                                                            key={indexUnitBasicInformation}
                                                        >
                                                            <span
                                                                className={`facility-unit__description-box-svg ${itemUnitBasicInformation.icon.sourceURL}`}
                                                                aria-hidden="true"
                                                            />
                                                            <span className="sr-only">{itemUnitBasicInformation.icon.altText}</span>
                                                            <span
                                                                className="facility-unit__description-box-description"
                                                                dangerouslySetInnerHTML={{ __html: itemUnitBasicInformation.informationHTMLContent }}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {presentationUnit.overviewInformation.basicAmenitiesInfo && (
                                                <div className="facility-unit__description-popular-content d-flex flex-column">
                                                    {/* TODO: BACKEND - Po dizajnu je izbačen naslov u ovoj sekciji. */}
                                                    {/* <div className="facility-unit__description-popular-content-title">{presentationUnit.overviewInformation.basicAmenitiesInfo.title}</div> */}
                                                    {presentationUnit.overviewInformation.basicAmenitiesInfo.amenities?.length > 0 && (
                                                        <ul className="facility-unit__description-popular-content-items">
                                                            {presentationUnit.overviewInformation.basicAmenitiesInfo.amenities.map((amenitiesItem, indexBasicAmenitiesInfo) => (
                                                                <li
                                                                    key={indexBasicAmenitiesInfo}
                                                                    className="facility-unit__popular-content-item d-flex"
                                                                >
                                                                    {amenitiesItem.icon && (
                                                                        <span
                                                                            className={amenitiesItem.icon.sourceURL}
                                                                            aria-hidden="true"
                                                                        />
                                                                    )}
                                                                    <span className="facility-unit__popular-content-item-txt">
                                                                        {amenitiesItem.isAvailable
                                                                            ? (
                                                                                <>
                                                                                    {amenitiesItem.title}
                                                                                    {amenitiesItem.icon?.altText ? (
                                                                                        <span className="sr-only">{` - ${amenitiesItem.icon.altText}`}</span>
                                                                                    ) : null}
                                                                                </>
                                                                            )
                                                                            : (
                                                                                <>
                                                                                    <s>{amenitiesItem.title}</s>
                                                                                    <span className="sr-only">{` - ${presentationUnit.overviewInformation.basicAmenitiesInfo.notAvailableLabel}`}</span>
                                                                                </>
                                                                            )}
                                                                    </span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                    {presentationUnit.overviewInformation.basicAmenitiesInfo.loadMoreTitle && (
                                                        <button
                                                            type="button"
                                                            className="facility-unit__description-popular-content-more d-flex"
                                                            onClick={handleUnitAmenitiesModal}
                                                        >
                                                            {presentationUnit.overviewInformation.basicAmenitiesInfo.loadMoreTitle}
                                                            <MdArrowRight size={20} />
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div
                                            className="facility-unit__description-place d-flex flex-column"
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => handleUnitMapModal(UNIT_PRESENTATION_MAP_LOCATION_FROM_OVERVIEW_OPEN)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') handleUnitMapModal(UNIT_PRESENTATION_MAP_LOCATION_FROM_OVERVIEW_OPEN);
                                            }}
                                        >
                                            <div className="facility-unit__description-map">
                                                {presentationUnit.overviewInformation.showOnMapSmallImage && (
                                                    <img
                                                        loading="lazy"
                                                        className="facility-unit__description-map-image"
                                                        src={presentationUnit.overviewInformation.showOnMapSmallImage.sourceURL}
                                                        alt={presentationUnit.overviewInformation.showOnMapSmallImage.altText}
                                                        title={presentationUnit.overviewInformation.showOnMapSmallImage.titleText}
                                                    />
                                                )}
                                                <span className="facility-unit__description-map-btn d-flex align-items-center">
                                                    {/* TODO: DIZAJN - Pitat dizajnera name ikonice ako nije custom odrađena. */}
                                                    <MdLocationOn className="facility-unit__description-map-btn-icon" size={32} />
                                                    <span>{presentationUnit.overviewInformation.showOnMapTitle}</span>
                                                </span>
                                            </div>
                                            {presentationUnit.overviewInformation.locationBasedRatings?.length > 0 && (
                                                <div className="facility-unit__description-reviews">
                                                    {/* TODO: DIZAJN - Nije po dizajnu kad nije cili broj */}
                                                    {/* TODO: BACKEND - Dizajn trazi da se broj posalje kao float cak i ako je cili */}
                                                    {presentationUnit.overviewInformation.locationBasedRatings.map((locationBasedRatingsItem, indexLocationBasedRatings) => (
                                                        <div
                                                            key={indexLocationBasedRatings}
                                                            className="facility-unit__description-reviews-content d-flex justify-content-between"
                                                        >
                                                            <span className="facility-unit__reviews-txt">{locationBasedRatingsItem.title}</span>
                                                            <span className="facility-unit__reviews-txt facility-unit__reviews-txt--normal">
                                                                <MdOutlineStar className="facility-unit__reviews-star" size={20} />
                                                                <span>{locationBasedRatingsItem.averageGrade}</span>
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {/* Robinson overview */}
                            {presentationUnit.robinsonOverview && (
                                <div className="facility-unit__robinson d-flex flex-column">
                                    <div className="d-flex align-items-center">
                                        <MdOutlineNaturePeople className="facility-unit__robinson-icon" />
                                        <h2 className="facility-unit__detail-title facility-unit__robinson-title">
                                            {presentationUnit.robinsonOverview.title}
                                        </h2>
                                    </div>
                                    {isMobileClient ? (
                                        <div className="facility-unit__robinson-txt-wrap d-flex flex-column">
                                            <p className="facility-unit__robinson-txt">
                                                {renderRobinsonDescription()}
                                            </p>
                                            {presentationUnit.robinsonOverview.description.length > maxLengthForInitialRobinsonDescription && (
                                                <button
                                                    type="button"
                                                    className="facility-unit__robinson-btn"
                                                    onClick={() => setExpandedRobinson(!expandedRobinson)}
                                                >
                                                    {expandedRobinson ? (
                                                        <span>
                                                            {presentationUnit.robinsonOverview.showLessTitle}
                                                            <MdArrowDropUp size={20} />
                                                        </span>
                                                    ) : (
                                                        <span>
                                                            {presentationUnit.robinsonOverview.showMoreTitle}
                                                            <MdArrowDropDown size={20} />
                                                        </span>
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <p className="facility-unit__robinson-txt">
                                            {presentationUnit.robinsonOverview.description}
                                        </p>
                                    )}
                                    {presentationUnit.robinsonOverview.characteristics?.length > 0 && (
                                        <div className="facility-unit__robinson-box-wrap d-flex flex-column flex-md-row">
                                            {presentationUnit.robinsonOverview.characteristics.map((characteristicItem, characteristicKey) => (
                                                <div
                                                    className="facility-unit__robinson-box d-flex flex-row flex-md-column justify-content-between justify-content-md-start align-items-center"
                                                    key={characteristicKey}
                                                >
                                                    <div className="facility-unit__robinson-box-title d-flex flex-column align-items-md-center">
                                                        {characteristicItem.icon && (
                                                            <img
                                                                loading="lazy"
                                                                src={characteristicItem.icon.sourceURL}
                                                                alt={characteristicItem.icon.altText}
                                                                title={characteristicItem.icon.titleText}
                                                                width={20}
                                                                height={20}
                                                            />
                                                        )}
                                                        <p className="facility-unit__robinson-box-title">
                                                            {characteristicItem.title}
                                                        </p>
                                                    </div>
                                                    <ul className="facility-unit__robinson-box-list d-flex flex-column justify-content-center">
                                                        {Object.keys(characteristicItem.items).map((key, i) => (
                                                            <li className="facility-unit__robinson-box-item" key={i}>
                                                                {characteristicItem.items[key]}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                            {/* Room details */}
                            {presentationUnit.roomDetails && (
                                <div
                                    className="facility-unit__detail-box facility-unit__rooms"
                                    // TODO: BACKEND - Provjerit sa backendom zasto nema id-a sekcije (potreban za scroll iz menu-a).
                                    // Provjereno sa backendom, id ce imati samo 4 sekcije, uklonit id sa elemenata koji nemaju ID.
                                    id={presentationUnit.roomDetails.sectionId}
                                >
                                    <div className="facility-unit__rooms-header d-flex align-items-center justify-content-between">
                                        <h2 className="facility-unit__detail-title">{presentationUnit.roomDetails.title}</h2>
                                        {presentationUnit.roomDetails.totalAreaSquareMeters && (
                                            <span className="d-none d-md-block">
                                                {presentationUnit.roomDetails.totalAreaTitle}
                                                :&nbsp;
                                                <strong dangerouslySetInnerHTML={{ __html: presentationUnit.roomDetails.totalAreaSquareMeters }} />
                                            </span>
                                        )}
                                    </div>
                                    {presentationUnit.roomDetails.rooms?.length > 0 && (
                                        <div className="facility-unit__rooms-boxes d-flex flex-wrap">
                                            {/* TODO: DIZAJN - Vidit sa dizajnerom kakva je animacija. */}
                                            {presentationUnit.roomDetails.rooms.slice(0, visibleRoomDetailsRoomBoxes).map((itemRoom, indexRoom) => (
                                                <div
                                                    key={indexRoom}
                                                    className="facility-unit__rooms-box"
                                                >
                                                    <UnitRoomBox data={itemRoom} />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div className="facility-unit__rooms-box-btn-wrapp d-flex justify-content-center">
                                        {presentationUnit.roomDetails.rooms?.length > initialVisibleRooms && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowAllRoomDetailsRoomBoxes((prevState) => {
                                                        const isOpen = !prevState;
                                                        if (!IS_FRAME_SERVICE && isOpen) {
                                                            dispatchTrackingEvent(UNIT_PRESENTATION_ROOM_DETAILS_OPEN, {
                                                                unitID: mainUnitID,
                                                            });
                                                        }
                                                        return isOpen;
                                                    });
                                                }}
                                                className="facility-unit__rooms-box-btn"
                                            >
                                                {showAllRoomDetailsRoomBoxes ? (
                                                    <span className="facility-unit__rooms-box-btn-txt d-flex">
                                                        {presentationUnit.roomDetails.viewLessTitle}
                                                        <MdArrowDropUp size={20} />
                                                    </span>
                                                ) : (
                                                    <span className="facility-unit__rooms-box-btn-txt d-flex">
                                                        {presentationUnit.roomDetails.viewMoreTitle}
                                                        <MdArrowDropDown size={20} />
                                                    </span>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                    {presentationUnit.roomDetails.agencyNotes && (
                                        <div className="facility-unit__rooms-footer d-flex flex-column">
                                            <p className="facility-unit__rooms-footer-title">{presentationUnit.roomDetails.agencyNotes.title}</p>
                                            {presentationUnit.roomDetails.agencyNotes.notes.map((itemAgencyNote, indexAgencyNote) => (
                                                <p key={indexAgencyNote} className="facility-unit__rooms-footer-txt">
                                                    {itemAgencyNote}
                                                </p>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                            {/* Pricelist and availability */}
                            {presentationUnit.pricelistAndAvailability && (
                                <div
                                    className="facility-unit__detail-box facility-unit__pricelist d-flex flex-column"
                                    // TODO: BACKEND - Provjerit sa backendom zasto nema id-a sekcije (potreban za scroll iz menu-a).
                                    // Provjereno sa backendom, id ce imati samo 4 sekcije, uklonit id sa elemenata koji nemaju ID.
                                    id={presentationUnit.pricelistAndAvailability.sectionId}
                                    ref={datePickerRef}
                                >
                                    <div className="facility-unit__pricelist__header d-flex flex-column flex-md-row justify-content-between align-items-md-center">
                                        <h2 className="facility-unit__detail-title">
                                            {presentationUnit.pricelistAndAvailability.title}
                                        </h2>
                                        <div className={`facility-unit__pricelist-header-persons-wrap ${isLoadingUnitPriceAvailabilityCalendar ? 'disableCurrentElementContent--transparent' : ' '} d-flex justify-content-between justify-content-md-start align-items-center`}>
                                            <p className="facility-unit__pricelist-header-persons-text">{presentationUnit.pricelistAndAvailability.numberOfPersonsText}:</p>
                                            {unitPriceAvailabilityCalendar && (
                                                <div className="facility-unit__pricelist-header-persons-boxes d-flex">
                                                    {Array.from(
                                                        { length: unitPriceAvailabilityCalendar.unitMaxPriceCapacity - unitPriceAvailabilityCalendar.unitMinPriceCapacity + 1 },
                                                        (_, i) => {
                                                            const capacity = unitPriceAvailabilityCalendar.unitMinPriceCapacity + i;
                                                            const isActive = capacity === unitPriceAvailabilityCalendar.selectedPersonNumber;
                                                            return (
                                                                <button
                                                                    key={capacity}
                                                                    className={`facility-unit__pricelist-header-persons-box d-flex justify-content-center align-items-center ${isActive ? ' facility-unit__pricelist-header-persons-box--active' : ''}`}
                                                                    onClick={() => {
                                                                        setSelectedPersonNumber(capacity);
                                                                        if (!IS_FRAME_SERVICE) {
                                                                            dispatchTrackingEvent(UNIT_PRESENTATION_PRICELIST_CAPACITY_SELECTED, {
                                                                                unitID: mainUnitID,
                                                                            });
                                                                        }
                                                                    }}
                                                                    type="button"
                                                                >
                                                                    {capacity}
                                                                </button>
                                                            );
                                                        },
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className={`facility-unit__pricelist-date-picker d-flex flex-column ${isLoadingUnitPriceAvailabilityCalendar ? 'disableCurrentElementContent--spinner' : ' '}`}>
                                        <div className="facility-unit__pricelist-date-picker-header d-flex justify-content-between">
                                            <div className="facility-unit__pricelist-date-picker-header-info d-flex flex-column">
                                                {startDate && endDate && unitPriceCalculation?.simpleReservationFormCalculatedInformation?.numberOfNights ? (
                                                    <p className="facility-unit__pricelist-date-picker-header-info-title">
                                                        <span>
                                                            <span>{presentationUnit.simpleReservationFormLabels.nuberOfNightsTitle}:&nbsp;</span>
                                                            <span>{unitPriceCalculation.simpleReservationFormCalculatedInformation.numberOfNights}</span>
                                                        </span>
                                                    </p>
                                                ) : (
                                                    <p className="facility-unit__pricelist-date-picker-header-info-title">
                                                        {startDate ? (
                                                            <span>
                                                                {presentationUnit.simpleReservationFormLabels.endDateInfoText}
                                                            </span>
                                                        ) : (
                                                            <span>
                                                                {presentationUnit.simpleReservationFormLabels.startDateInfoText}
                                                            </span>
                                                        )}
                                                    </p>
                                                )}
                                                <p className="facility-unit__pricelist-date-picker-header-info-txt">
                                                    {startDate ? (
                                                        <span>
                                                            {startDateFormatted}
                                                        </span>
                                                    ) : (
                                                        <span>
                                                            {presentationUnit.simpleReservationFormLabels.arrivalTitle}
                                                        </span>
                                                    )}
                                                    <span>&nbsp;-&nbsp;</span>
                                                    {endDate ? (
                                                        <span>
                                                            {endDateFormatted}
                                                        </span>
                                                    ) : (
                                                        <span>
                                                            {presentationUnit.simpleReservationFormLabels.departureTitle}
                                                        </span>
                                                    )}
                                                </p>
                                            </div>
                                            <div
                                                className="facility-unit__pricelist-date-picker-header-dates d-none d-md-flex align-items-center"
                                                role="button"
                                                tabIndex={0}
                                                onClick={handleReservationFormClickDates}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') handleReservationFormClickDates();
                                                }}
                                            >
                                                <div className="facility-unit__pricelist-date-picker-header-date-box">
                                                    <p className="facility-unit__pricelist-date-picker-header-date-box-title">
                                                        {presentationUnit.simpleReservationFormLabels.arrivalTitle}
                                                    </p>
                                                    {startDate ? (
                                                        <p className="facility-unit__pricelist-date-picker-header-date-box-date">
                                                            {startDateFormatted}
                                                        </p>
                                                    ) : (
                                                        <p className="facility-unit__pricelist-date-picker-header-date-box-date">
                                                            -
                                                        </p>
                                                    )}
                                                </div>
                                                <MdChevronRight className="facility-unit__pricelist-date-picker-header-date-icon" />
                                                <div className="facility-unit__pricelist-date-picker-header-date-box">
                                                    <p className="facility-unit__pricelist-date-picker-header-date-box-title">
                                                        {presentationUnit.simpleReservationFormLabels.departureTitle}
                                                    </p>
                                                    {endDate ? (
                                                        <p className="facility-unit__pricelist-date-picker-header-date-box-date">
                                                            {endDateFormatted}
                                                        </p>
                                                    ) : (
                                                        <p className="facility-unit__pricelist-date-picker-header-date-box-date">
                                                            -
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        {renderDateRangePickerView()}
                                        <div className="facility-unit__pricelist-date-picker-footer d-flex justify-content-end">
                                            <button
                                                className="adr-btn-link-main"
                                                type="button"
                                                onClick={() => handleDatesChange('', '')}
                                                aria-label="Remove reservation dates"
                                            >
                                                {presentationUnit.simpleReservationFormLabels.clearSelectedDatesInfoText}
                                            </button>
                                        </div>
                                    </div>
                                    { renderReservation() }
                                    {/* Odrađeno preko id-a jer se pojavljuje na pageu i u komponenti. */}
                                    <div className="facility-unit__pricelist-info">
                                        {/* TODO: Dok ne dođe dokumentacija bilo bi dobro provjerit jesu li handle-ani svi mogući podaci iz "pricelistAndAvailability". */}
                                        {presentationUnit.pricelistAndAvailability.discounts && (
                                            renderPriceListAndAvailabilityInfoView(presentationUnit.pricelistAndAvailability.discounts)
                                        )}
                                        {presentationUnit.pricelistAndAvailability.additionalDiscounts && (
                                            renderPriceListAndAvailabilityInfoView(presentationUnit.pricelistAndAvailability.additionalDiscounts)
                                        )}
                                        {/* TODO: DIZAJN - Ovaj dio ne moze ici kao po dizajnu pod "Cijene su umanjene za:" zato sto dolazi odvojeno i moze se desiti da ne postoji dio "Cijene su umanjene za:" i samim time ne bi se prikazao ni ovaj dio (refactor ili dizajna ili backedna ako ne odgovara ovako kako je napravljeno) . */}
                                        {presentationUnit.pricelistAndAvailability.rainyDayRefund && (
                                            renderPriceListAndAvailabilityRainyDayRefundView(presentationUnit.pricelistAndAvailability.rainyDayRefund)
                                        )}
                                        {presentationUnit.pricelistAndAvailability.includedInPrice && (
                                            renderPriceListAndAvailabilityIncludedInPriceView(presentationUnit.pricelistAndAvailability.includedInPrice)
                                        )}
                                        {presentationUnit.pricelistAndAvailability.optionalExpenses && (
                                            renderPriceListAndAvailabilityInfoView(presentationUnit.pricelistAndAvailability.optionalExpenses)
                                        )}
                                        {(presentationUnit.pricelistAndAvailability.shortStayExtraCharge
                                        || presentationUnit.pricelistAndAvailability.touristTaxInfo) && (
                                            <div className="facility-unit__pricelist-info-box d-flex flex-column">
                                                {presentationUnit.pricelistAndAvailability.shortStayExtraCharge && (
                                                    renderPriceListAndAvailabilityShortStayExtraChargeView(presentationUnit.pricelistAndAvailability.shortStayExtraCharge)
                                                )}
                                                {presentationUnit.pricelistAndAvailability.touristTaxInfo && (
                                                    renderPriceListAndAvailabilityTouristTaxInfoView(presentationUnit.pricelistAndAvailability.touristTaxInfo)
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            {/* Facility units */}
                            {/* Prikazujemo kartice i povezane informacije samo ako postoji više od jedne jedinice. */}
                            {presentationUnit?.units?.unitsShortInfo?.length > 1 && (
                                <div
                                    className="facility-unit__detail-box facility-unit__detail-box--transparent facility-unit__units d-flex flex-column"
                                    ref={unitsShortInfoRef}
                                >
                                    <div className="facility-unit__units__header d-flex justify-content-between">
                                        <h2 className="facility-unit__units-header-title d-flex align-items-center">
                                            {/* TODO: DIZAJN BACKEND - Ne dolazi mi ikonica */}
                                            {/* <MdOutlineHouse size={30} color='#fd0505'/> */}
                                            <span>&#127969;</span>
                                            {presentationUnit.units.title}
                                        </h2>
                                        <div className="facility-unit__units-header-txt d-none d-md-flex align-items-center">
                                            <p>{presentationUnit.units.totalText}:&nbsp;</p>
                                            <span className="facility-unit__units-header-txt-value">
                                                {presentationUnit.units.unitsShortInfo.length}
                                            </span>
                                        </div>
                                    </div>
                                    {presentationUnit.units.unitTypesCount && (
                                        <div className="facility-unit__units-navigation d-flex">
                                            {presentationUnit.units.unitTypesCount.length > 0
                                            && presentationUnit.units.unitTypesCount.map((itemUnitType, indexUnitType) => (
                                                <div
                                                    key={indexUnitType}
                                                    className="facility-unit__units-navigation-item d-flex align-items-center"
                                                >
                                                    {/* TODO: NEDOVRŠENO - Postoji i type, provjerit njegovu svrhu ??? */}
                                                    <p>{itemUnitType.title}</p>
                                                    <p className="facility-unit__units-navigation-item-circle d-flex justify-content-center align-items-center">
                                                        <span className="facility-unit__units-navigation-item-circle-txt">
                                                            {itemUnitType.numberOfUnits}
                                                        </span>
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {presentationUnit.units.unitsShortInfo && (
                                        // Napomena da prikaz kartica na CSR-u moze doci naknadno zbog toga što order ovisi o unitPriceCalculation a on se requesta nakon glavnog request-a.
                                        <div className={`facility-unit__units-card-wrap d-flex flex-column ${isLoadingUnitPriceCalculation ? 'disableCurrentElementContent--spinner' : ' '}`}>
                                            {unitPriceCalculation ? presentationUnit.units.unitsShortInfo
                                                // Mapiramo "presentationUnit.units.unitsShortInfo" ali sortiramo jedinice na osnovu "unitPriceCalculation.units"
                                                .slice()
                                                .sort((a, b) => {
                                                    const orderMap = unitPriceCalculation.units.reduce((acc, unit, index) => {
                                                        acc[unit.unitId] = index;
                                                        return acc;
                                                    }, {});
                                                    return orderMap[a.unitId] - orderMap[b.unitId];
                                                })
                                                .filter((item) => showAllUnitCards || visibleUnitCardIds.includes(item.unitId))
                                                .map((itemUnitsShortInfo) => (
                                                    // DESC: Prikaz kartica samo za jedinice koje dolaze i sa API-a za kalkulaciju.
                                                    unitPriceCalculationUnitsById[itemUnitsShortInfo.unitId] && (
                                                        <FacilityUnitCard
                                                            key={itemUnitsShortInfo.unitId}
                                                            data={itemUnitsShortInfo}
                                                            sharedData={sharedUnitData}
                                                            isMobileClient={isMobileClient}
                                                            currentReservationParameters={reservationParameters}
                                                            fetchForID={currentRequestedUnitID}
                                                            priceData={unitPriceCalculationUnitsById[itemUnitsShortInfo.unitId] || null}
                                                            selectHandler={handleSelectAnotherUnit}
                                                            modalHandler={handleSeeMoreAboutUnitModal}
                                                            guestSelectHandler={openGuestSelectModal}
                                                            isLoading={isLoadingMoreAboutUnit}
                                                        />
                                                    )
                                                ))
                                                : (
                                                    renderFacilityUnitCardSkeleton()
                                                )}
                                            {(unitPriceCalculation && (missingUnitIdsInUnitPriceCalculation.length === 0))
                                            && (presentationUnit.units.unitsShortInfo?.length > initialVisibleUnitCards) && (
                                                <button
                                                    type="button"
                                                    className="facility-unit__units-card-toggle-btn d-flex justify-content-center"
                                                    onClick={() => setShowAllUnitCards(!showAllUnitCards)}
                                                >
                                                    {showAllUnitCards ? (
                                                        <span className="facility-unit__units-card-toggle-btn-txt d-flex">
                                                            {presentationUnit.units.showLessUnitsText}
                                                            <MdArrowDropUp size={20} />
                                                        </span>
                                                    ) : (
                                                        <span className="facility-unit__units-card-toggle-btn-txt d-flex">
                                                            {presentationUnit.units.showAllUnitsText}
                                                            <MdArrowDropDown size={20} />
                                                        </span>
                                                    )}
                                                </button>
                                            )}
                                            {presentationUnit.units.unitsShortInfo?.length > initialVisibleUnitCards
                                            && !unitPriceCalculation
                                            && isLoadingUnitPriceCalculation && (
                                                <div className="facility-unit__units-card-toggle-btn d-flex justify-content-center">
                                                    <Skeleton width={120} height={30} />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                            {/* Facility description */}
                            {presentationUnit.facilityDescription && (
                                <div
                                    className="facility-unit__detail-box facility-unit__detail-box--mob-full facility-unit__more-description"
                                    // TODO: BACKEND - Provjerit sa backendom zasto nema id-a sekcije (potreban za scroll iz menu-a).
                                    // Provjereno sa backendom, id ce imati samo 4 sekcije, uklonit id sa elemenata koji nemaju ID.
                                    id={presentationUnit.facilityDescription.sectionId}
                                >
                                    <h2
                                        className="facility-unit__detail-title facility-unit__detail-title--upper facility-unit__more-description-title speakable-content"
                                    >
                                        {presentationUnit.facilityDescription.title}
                                    </h2>
                                    <div
                                        className="facility-unit__more-description-txt speakable-content"
                                        dangerouslySetInnerHTML={{ __html: presentationUnit.facilityDescription.descriptionHTMLContent }}
                                    />
                                </div>
                            )}
                            {/* NASTAVIT - /rsb-service/accommodation/facility-reviews/hr/14247/0/10 */}
                            {/* Reviews */}
                            {presentationUnit.reviews && (
                                <div className="facility-unit__detail-box facility-unit__detail-box--mob-full facility-unit__reviews d-flex flex-column">
                                    <div className="facility-unit__reviews__header d-flex justify-content-between align-items-center">
                                        <h2 className="facility-unit__detail-title">
                                            {presentationUnit.reviews.title}
                                        </h2>
                                        <div className="facility-unit__reviews-header-info d-flex">
                                            <p className="facility-unit__reviews-header-info-txt">
                                                {presentationUnit.reviews.accommodationReviewsTitle}
                                                {presentationUnit.reviews.averageAccommodationReviewsShortInfo.averageGrade && (
                                                    <span className="facility-unit__reviews-header-info-grade">
                                                        &nbsp;{presentationUnit.reviews.averageAccommodationReviewsShortInfo.averageGrade}
                                                    </span>
                                                )}
                                            </p>
                                            <p className="facility-unit__reviews-header-info-txt">
                                                {presentationUnit.reviews.providerReviewsTitle}
                                                {presentationUnit.reviews.averageProviderReviewsShortInfo.averageGrade && (
                                                    <span className="facility-unit__reviews-header-info-grade">
                                                        &nbsp;{presentationUnit.reviews.averageProviderReviewsShortInfo.averageGrade}
                                                    </span>
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                    {presentationUnit.reviews.reviewItems?.length > 0 && (
                                        <div className="facility-unit__reviews-content d-flex flex-column">
                                            {presentationUnit.reviews.reviewItems.map((itemReview, indexReview) => (
                                                <SingleReview key={indexReview} data={itemReview} replyControl={true} />
                                            ))}
                                        </div>
                                    )}
                                    {presentationUnit.reviews.loadMoreTitle && (
                                        <button
                                            type="button"
                                            className="facility-unit__reviews-content-btn d-flex align-items-center"
                                            onClick={() => handleReviewsModal(UNIT_PRESENTATION_REVIEWS_FROM_SHOW_MORE_OPEN)}
                                        >
                                            <span>{presentationUnit.reviews.loadMoreTitle}</span>
                                            <MdArrowRight size={20} />
                                        </button>
                                    )}
                                </div>
                            )}
                            {/* Location */}
                            {presentationUnit.location && (
                                <div
                                    className="facility-unit__detail-box facility-unit__detail-box--mob-full facility-unit__location"
                                    // TODO: BACKEND - Provjerit sa backendom zasto nema id-a sekcije (potreban za scroll iz menu-a).
                                    // Provjereno sa backendom, id ce imati samo 4 sekcije, uklonit id sa elemenata koji nemaju ID.
                                    id={presentationUnit.location.sectionId}
                                >
                                    <div className="facility-unit__location-header d-flex justify-content-between align-items-center">
                                        <div className="facility-unit__location-header-info">
                                            <h2 className="facility-unit__detail-title facility-unit__location-title">
                                                {presentationUnit.location.title}
                                            </h2>
                                            <p className="facility-unit__location-header-info-txt">
                                                {presentationUnit.location.placeName}
                                                {presentationUnit.location.rivieraName && (
                                                    <span>&#44;&nbsp;{presentationUnit.location.rivieraName}</span>
                                                )}
                                            </p>
                                        </div>
                                        <div
                                            className="facility-unit__location-header-map"
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => handleUnitMapModal(UNIT_PRESENTATION_MAP_LOCATION_FROM_LOCATION_OPEN)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') handleUnitMapModal(UNIT_PRESENTATION_MAP_LOCATION_FROM_LOCATION_OPEN);
                                            }}
                                        >
                                            {presentationUnit.location.showOnMapLargeImage && (
                                                <img
                                                    loading="lazy"
                                                    className="facility-unit__location-header-map-image"
                                                    width={522}
                                                    height={54}
                                                    src={presentationUnit.location.showOnMapLargeImage.sourceURL}
                                                    alt={presentationUnit.location.showOnMapLargeImage.altText}
                                                    title={presentationUnit.location.showOnMapLargeImage.titleText}
                                                />
                                            )}
                                            <span className="facility-unit__location-header-map-btn d-flex align-items-center">
                                                {/* TODO: DIZAJN - Pitat dizajnera name ikonice ako nije custom odrađena. */}
                                                <MdLocationOn className="facility-unit__location-header-map-btn-icon" size={24} />
                                                <span>{presentationUnit.overviewInformation.showOnMapTitle}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div className="facility-unit__location-more-info d-flex flex-column flex-md-row">
                                        {presentationUnit.location.microlocation && (
                                            <div className="facility-unit__location-more-info-microlocation d-flex flex-column">
                                                <h3 className="facility-unit__location-more-info-title">
                                                    {presentationUnit.location.microlocation.title}
                                                </h3>
                                                <div className="facility-unit__location-more-info__content">
                                                    {presentationUnit.location.microlocation.items?.length > 0
                                                    && presentationUnit.location.microlocation.items.map((
                                                        itemMicrolocation,
                                                        indexMicrolocation,
                                                    ) => (
                                                        <div
                                                            key={indexMicrolocation}
                                                            className={`facility-unit__location-more-info-content-item d-flex justify-content-between align-items-center ${itemMicrolocation.isChildItem ? 'facility-unit__location-more-info-content-item--indent' : ''}`}
                                                        >
                                                            <p>
                                                                {itemMicrolocation.title}
                                                            </p>
                                                            {itemMicrolocation.value ? (
                                                                <span>
                                                                    {itemMicrolocation.value}
                                                                </span>
                                                            ) : (
                                                                <span>
                                                                    {itemMicrolocation.isAvailable ? (
                                                                        <MdCheck className="location-more-info-content-item__available" />
                                                                    ) : (
                                                                        <MdClose className="location-more-info-content-item__not-available" />
                                                                    )}
                                                                </span>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {presentationUnit.location.distances && (
                                            <div className="facility-unit__location-more-info-distance d-flex flex-column">
                                                <h3 className="facility-unit__location-more-info-title">
                                                    {presentationUnit.location.distances.title}
                                                </h3>
                                                <div className="facility-unit__location-more-info__content">
                                                    {presentationUnit.location.distances.distanceItems?.length > 0
                                                    && presentationUnit.location.distances.distanceItems.slice(0, visibleDistanceItems).map((
                                                        itemDistance,
                                                        indexDistance,
                                                    ) => (
                                                        <div
                                                            key={indexDistance}
                                                            className="facility-unit__location-more-info-content-item d-flex justify-content-between align-items-center"
                                                        >
                                                            <div className="facility-unit__location-more-info-distance-icon-title d-flex">
                                                                {itemDistance.icon && (
                                                                    <img
                                                                        loading="lazy"
                                                                        src={itemDistance.icon.sourceURL}
                                                                        alt={itemDistance.icon.altText}
                                                                        title={itemDistance.icon.titleText}
                                                                        width={20}
                                                                        height={20}
                                                                    />
                                                                )}
                                                                <p>{itemDistance.title}</p>
                                                            </div>
                                                            <p>{itemDistance.value}</p>
                                                            {/* TODO: BACKEND - Pitat za sta je podatak "isChildItem" */}
                                                            {/* TODO: NEDOVRŠENO - Dodati funkcionalnost prikaži više/manje */}
                                                        </div>
                                                    ))}
                                                </div>
                                                {presentationUnit.location.distances.distanceItems?.length > initialVisibleRooms && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowAllDistanceItems((prevState) => !prevState)}
                                                        className="facility-unit__location-more-info-distance-btn"
                                                    >
                                                        {showAllDistanceItems ? (
                                                            <span className="facility-unit__location-more-info-distance-btn-txt d-flex justify-content-center align-items-center">
                                                                {presentationUnit.showLessText}
                                                                <MdArrowDropUp size={20} />
                                                            </span>
                                                        ) : (
                                                            <span className="facility-unit__location-more-info-distance-btn-txt d-flex justify-content-center align-items-center">
                                                                {presentationUnit.showMoreText}
                                                                <MdArrowDropDown size={20} />
                                                            </span>
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {presentationUnit.location.beaches && (
                                        <div className="facility-unit__location-beaches d-flex flex-column">
                                            <h3 className="facility-unit__location-beaches-title">{presentationUnit.location.beaches.title}</h3>
                                            <div className="facility-unit__location-beaches-controlls d-flex">
                                                {presentationUnit.location.beaches.beachItems?.closestBeach?.title && (
                                                    <button
                                                        className={`facility-unit__location-beaches-left-btn ${activeLocationBeaches === CLOSEST_BEACH ? 'active-beach-btn' : ''}`}
                                                        onClick={() => setActiveLocationBeaches(CLOSEST_BEACH)}
                                                        type="button"
                                                    >
                                                        {presentationUnit.location.beaches.beachItems.closestBeach.title}
                                                    </button>
                                                )}
                                                {presentationUnit.location.beaches.beachItems?.otherBeaches?.title && (
                                                    <button
                                                        className={`facility-unit__location-beaches-right-btn ${activeLocationBeaches === OTHER_BEACHES ? 'active-beach-btn' : ''}`}
                                                        onClick={() => {
                                                            setActiveLocationBeaches(OTHER_BEACHES);
                                                            if (!IS_FRAME_SERVICE) {
                                                                dispatchTrackingEvent(UNIT_PRESENTATION_OTHER_BEACHES_OPEN, {
                                                                    unitID: mainUnitID,
                                                                });
                                                            }
                                                        }}
                                                        type="button"
                                                    >
                                                        {presentationUnit.location.beaches.beachItems.otherBeaches.title}
                                                    </button>
                                                )}
                                            </div>
                                            {/* TODO: #58308 Privremeno, nakon comitt-a prve verzije će se uklonit ili doradit. NEDOVRŠENO - DODAT TRANZICIJU */}
                                            {activeLocationBeaches === CLOSEST_BEACH
                                            && renderBeachContentView(presentationUnit.location.beaches.beachItems.closestBeach)}
                                            {activeLocationBeaches === OTHER_BEACHES
                                            && renderBeachContentView(presentationUnit.location.beaches.beachItems.otherBeaches)}
                                        </div>
                                    )}
                                </div>
                            )}
                            {/* Polices */}
                            {presentationUnit.policies && (
                                <div
                                    className="facility-unit__detail-box facility-unit__detail-box--mob-full facility-unit__policies d-flex flex-column"
                                    // TODO: BACKEND - Provjerit sa backendom zasto nema id-a sekcije (potreban za scroll iz menu-a).
                                    // Provjereno sa backendom, id ce imati samo 4 sekcije, uklonit id sa elemenata koji nemaju ID.
                                    id={presentationUnit.policies.sectionId}
                                >
                                    <h2
                                        className="facility-unit__detail-title facility-unit__policies-title"
                                    >
                                        {presentationUnit.policies.title}
                                    </h2>
                                    {presentationUnit.policies.freeCancellation && (
                                        <div className="facility-unit__policies-free-cancel d-flex flex-column">
                                            <h3 className="facility-unit__policies-free-cancel-title">
                                                {presentationUnit.policies.freeCancellation.title}
                                            </h3>
                                            <p className="facility-unit__policies-free-cancel-txt">
                                                {presentationUnit.policies.freeCancellation.description}
                                            </p>
                                        </div>
                                    )}
                                    {presentationUnit.policies.amenitiesIncludedInPrice && (
                                        renderPoliciesSectionView(presentationUnit.policies.amenitiesIncludedInPrice)
                                    )}
                                    {presentationUnit.policies.mandatoryAdditionalCosts && (
                                        renderPoliciesSectionView(presentationUnit.policies.mandatoryAdditionalCosts, 'mandatoryAdditionalCosts')
                                    )}
                                    {presentationUnit.policies.otherPolicies && (
                                        renderPoliciesSectionView(presentationUnit.policies.otherPolicies, 'otherPolicies')
                                    )}
                                    {presentationUnit.policies.optionalAdditionalCosts && (
                                        renderPoliciesSectionView(presentationUnit.policies.optionalAdditionalCosts)
                                    )}
                                </div>
                            )}
                            {/* Hosue Rules */}
                            {presentationUnit.houseRules && (
                                <div
                                    className="facility-unit__detail-box facility-unit__detail-box--mob-full facility-unit__house-rules d-flex flex-column"
                                    // TODO: BACKEND - Provjerit sa backendom zasto nema id-a sekcije (potreban za scroll iz menu-a).
                                    // Provjereno sa backendom, id ce imati samo 4 sekcije, uklonit id sa elemenata koji nemaju ID.
                                    id={presentationUnit.houseRules.sectionId}
                                >
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h2
                                            className="facility-unit__detail-title facility-unit__house-rules-title"
                                        >
                                            {presentationUnit.houseRules.title}
                                        </h2>
                                        {presentationUnit.houseRules.agencyHouseRulesLink && (
                                            <a
                                                className="facility-unit__house-rules-link d-flex align-items-center"
                                                href={presentationUnit.houseRules.agencyHouseRulesLink.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <span><MdInsertLink size={20} /></span>
                                                <span className="facility-unit__house-rules-link-title">
                                                    {presentationUnit.houseRules.agencyHouseRulesLink.title}
                                                </span>
                                            </a>
                                        )}
                                    </div>
                                    <div className="facility-unit__house-rules-content d-flex flex-column flex-md-row">
                                        <div className="facility-unit__house-rules-content-time d-flex flex-column">
                                            {presentationUnit.houseRules.checkIn && (
                                                <div className="facility-unit__house-rules-content-time-check d-flex flex-column">
                                                    <p className="facility-unit__house-rules-content-time-check-title d-flex">
                                                        <MdOutlineQueryBuilder className="house-rules-content-time-check-title__green-icon" />
                                                        <span>{presentationUnit.houseRules.checkIn.title}</span>
                                                    </p>
                                                    <p
                                                        dangerouslySetInnerHTML={{ __html: presentationUnit.houseRules.checkIn.value }}
                                                    />
                                                </div>
                                            )}
                                            {presentationUnit.houseRules.checkOut && (
                                                <div className="facility-unit__house-rules-content-time-check d-flex flex-column">
                                                    <p className="facility-unit__house-rules-content-time-check-title d-flex">
                                                        <MdOutlineQueryBuilder className="house-rules-content-time-check-title__blue-icon" />
                                                        <span>{presentationUnit.houseRules.checkOut.title}</span>
                                                    </p>
                                                    <p
                                                        dangerouslySetInnerHTML={{ __html: presentationUnit.houseRules.checkOut.value }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        <div className="facility-unit__house-rules-content-info d-flex justify-content-between flex-column">
                                            {presentationUnit.houseRules.items?.length > 0 && (
                                                <div>
                                                    {presentationUnit.houseRules.items.map((itemRule, indexRule) => (
                                                        <p
                                                            key={indexRule}
                                                            className="facility-unit__house-rules-content-info-title d-flex align-items-center"
                                                        >
                                                            <span className="facility-unit__house-rules-content-info-title-dot" />
                                                            <span>{itemRule.title}</span>
                                                        </p>
                                                    ))}
                                                </div>
                                            )}
                                            {presentationUnit.houseRules.footNotes?.length > 0 && (
                                                <div>
                                                    {presentationUnit.houseRules.footNotes.map((itemFootNote, indexFootNote) => (
                                                        <p key={indexFootNote} className="facility-unit__house-rules-content-info-txt">
                                                            {itemFootNote.title}
                                                        </p>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {presentationUnit.faq?.questions && (
                                <FacilityUnitFAQ data={presentationUnit.faq} />
                            )}
                        </div>
                    </article>
                    {/* CONTENT 30% desktop, ne prikazuje se na mobile. */}
                    <article className="col-lg-4">
                        <div className="facility-unit__info-content">
                            {keepStickyVisibleForSelectorOnDesktop('.facility-unit__info-content')}
                            {presentationUnit.simpleReservationFormLabels && (
                                <div className={`facility-unit__reservation-box d-none d-lg-flex flex-column ${isLoadingUnitPriceCalculation ? 'disableCurrentElementContent--spinner' : ' '}`}>
                                    <div className="facility-unit__reservation-box-form d-flex flex-column">
                                        <div
                                            className="facility-unit__reservation-box-form-info"
                                        >
                                            <div
                                                className="facility-unit__reservation-box-form-dates d-flex justify-content-between"
                                                role="button"
                                                tabIndex={0}
                                                onClick={handleReservationFormClickDates}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') handleReservationFormClickDates();
                                                }}
                                            >
                                                <div className="facility-unit__reservation-box-form-dates-info d-flex align-items-center">
                                                    <HiOutlineCalendar className="facility-unit__reservation-box-form-dates-info-icon" />
                                                    <div className="facility-unit__reservation-box-form-dates-info-txt-wrap">
                                                        <p className="facility-unit__reservation-box-form-dates-info-txt">
                                                            {presentationUnit.simpleReservationFormLabels.arrivalTitle}
                                                        </p>
                                                        {startDate ? (
                                                            <p className="facility-unit__reservation-box-form-dates-info-txt-bold">
                                                                {startDateFormatted}
                                                            </p>
                                                        ) : (
                                                            <p className="facility-unit__reservation-box-form-dates-info-txt-bold">
                                                                {presentationUnit.simpleReservationFormLabels.dateInputPlaceholder}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="facility-unit__reservation-box-form-dates-info d-flex align-items-center">
                                                    <HiOutlineCalendar className="facility-unit__reservation-box-form-dates-info-icon" />
                                                    <div className="facility-unit__reservation-box-form-dates-info-txt-wrap">
                                                        <p className="facility-unit__reservation-box-form-dates-info-txt">
                                                            {presentationUnit.simpleReservationFormLabels.departureTitle}
                                                        </p>
                                                        {endDate ? (
                                                            <p className="facility-unit__reservation-box-form-dates-info-txt-bold">
                                                                {endDateFormatted}
                                                            </p>
                                                        ) : (
                                                            <p className="facility-unit__reservation-box-form-dates-info-txt-bold">
                                                                {presentationUnit.simpleReservationFormLabels.dateInputPlaceholder}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div
                                                className="facility-unit__reservation-box-form-guests d-flex align-items-center"
                                                role="button"
                                                tabIndex={0}
                                                onClick={handleReservationFormClickGuests}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') handleReservationFormClickGuests();
                                                }}
                                            >
                                                <MdOutlinePersonAddAlt className="facility-unit__reservation-box-form-guests-icon" />
                                                <div className="facility-unit__reservation-box-form-guests__content d-flex flex-column">
                                                    <p className="facility-unit__reservation-box-form-dates-info-txt">
                                                        {presentationUnit.simpleReservationFormLabels.guestsTitle}
                                                    </p>
                                                    {renderGuestsDisplayText()}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="facility-unit__reservation-box-form__footer">
                                            {unitPriceCalculation ? (
                                                renderFinalSelectedUnitsView(
                                                    hasSelectedUnitsFromUnitPriceCalculation
                                                        ? unitPriceCalculation.units.filter((item) => item.selected)
                                                        : selectedUnitsAlternative || [],
                                                )
                                            ) : (
                                                renderFinalSelectedUnitsSkeletonView()
                                            )}
                                            {!unitPriceCalculation && isLoadingUnitPriceCalculation ? (
                                                <div className="facility-unit__reservation-box-form-footer-price d-flex justify-content-between">
                                                    <div className="facility-unit__reservation-box-form-footer-price-txt facility-unit__reservation-box-form-footer-calculation-btn d-flex flex-column align-items-start">
                                                        <Skeleton width={90} height={18} />
                                                        <Skeleton width={60} height={14} />
                                                    </div>

                                                    <div className="d-flex flex-column align-items-end">
                                                        <Skeleton width={75} height={16} />
                                                        <Skeleton width={105} height={22} />
                                                    </div>
                                                </div>
                                            ) : (
                                                (unitPriceCalculation?.simpleReservationFormCalculatedInformation?.reservationFormError
                                                || unitPriceCalculation?.simpleReservationFormCalculatedInformation?.totalPriceValueInEUR) && (
                                                    unitPriceCalculation?.simpleReservationFormCalculatedInformation?.reservationFormError ? (
                                                        <div className="facility-unit__reservation-box-form-footer-price d-flex justify-content-between">
                                                            <p className="facility-unit__reservation-box-form-footer-price-error d-flex align-items-center">
                                                                {unitPriceCalculation?.simpleReservationFormCalculatedInformation?.reservationFormError}
                                                            </p>
                                                        </div>
                                                    ) : (
                                                        <div
                                                            className="facility-unit__reservation-box-form-footer-price facility-unit__reservation-box-form-footer-price--hover d-flex justify-content-between"
                                                            role="button"
                                                            tabIndex={0}
                                                            onClick={() => { handlePaymentDetailsModal(); }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter' || e.key === ' ') handlePaymentDetailsModal();
                                                            }}
                                                        >
                                                            {unitPriceCalculation?.simpleReservationFormCalculatedInformation?.totalPriceValueInEUR
                                                            && (unitPriceCalculation.simpleReservationFormCalculatedInformation.savingsPercent
                                                            || unitPriceCalculation.simpleReservationFormCalculatedInformation.savingsAmount)
                                                            && (
                                                                <div className="facility-unit__reservation-box-form-footer-price-txt facility-unit__reservation-box-form-footer-calculation-btn d-flex flex-column align-items-start">
                                                                    <span>{presentationUnit.simpleReservationFormLabels.totalPriceTitle}</span>
                                                                    <div className="facility-unit__reservation-box-form-footer-calculation-wrap">
                                                                        <span className="facility-unit__reservation-box-form-footer-calculation-box">
                                                                            {unitPriceCalculation.simpleReservationFormCalculatedInformation.savingsPercent}
                                                                        </span>
                                                                        <div className="facility-unit__reservation-box-form-footer-calculation-txt">
                                                                            <span>{presentationUnit.simpleReservationFormLabels.save}</span>
                                                                            &nbsp;
                                                                            <span>{unitPriceCalculation.simpleReservationFormCalculatedInformation.savingsAmount}</span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {unitPriceCalculation?.simpleReservationFormCalculatedInformation?.totalPriceValueInEUR && (
                                                                <div className="d-flex flex-column">
                                                                    {unitPriceCalculation?.simpleReservationFormCalculatedInformation?.totalPriceValueInEURWithoutDiscounts && (
                                                                        <p className="facility-unit__reservation-box-form-footer-price-txt--no-discount">
                                                                            {unitPriceCalculation.simpleReservationFormCalculatedInformation.totalPriceValueInEURWithoutDiscounts}
                                                                        </p>
                                                                    )}
                                                                    <p className="facility-unit__reservation-box-form-footer-price-txt">
                                                                        {unitPriceCalculation?.simpleReservationFormCalculatedInformation?.totalPriceValueInEUR}
                                                                    </p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )
                                                )
                                            )}
                                            {unitPriceCalculation?.simpleReservationFormCalculatedInformation?.totalPriceValueInEUR && (
                                                <div
                                                    className="facility-unit__reservation-box-form-btn"
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => { handlePaymentDetailsModal(); }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') handlePaymentDetailsModal();
                                                    }}
                                                >
                                                    {!unitPriceCalculation && isLoadingUnitPriceCalculation ? (
                                                        <Skeleton width={120} />
                                                    ) : (
                                                        <span className="facility-unit__reservation-box-form-btn-main">
                                                            <MdOutlineReceiptLong className="reservation-box-form-btn-main__icon" />
                                                            <span className="facility-unit__reservation-box-form-btn-header">
                                                                <span
                                                                    className="facility-unit__reservation-box-form-btn-title"
                                                                >
                                                                    {presentationUnit.simpleReservationFormLabels.showDetailedCalculation}
                                                                </span>
                                                                <span className="facility-unit__reservation-box-form-btn-subtitle">{presentationUnit.simpleReservationFormLabels.howIsThisPriceCalculated}</span>
                                                            </span>
                                                        </span>
                                                    )}
                                                    <MdOpenInNew className="facility-unit__reservation-box-form-btn-icon" />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {unitPriceCalculation?.simpleReservationFormCalculatedInformation?.reservationFormError ? (
                                        <button
                                            className="adr-btn-primary-lg"
                                            type="button"
                                            onClick={() => {
                                                handleClickStartReservation();
                                                handleTrackerReservationTextCTA();
                                                if (!IS_FRAME_SERVICE) {
                                                    triggerViewCart();
                                                }
                                            }}
                                            // TODO: #58308 Privremeno, potrebno ispratit ovu logiku sa stare implementacije.
                                        >
                                            <span>
                                                {reservationButtonTextCTA || presentationUnit.simpleReservationFormLabels.checkAvailabilityTitle}
                                            </span>
                                            {/* {presentationUnit.simpleReservationFormLabels.checkAvailabilityTitle} */}
                                        </button>
                                    ) : (
                                        <a
                                            // className={`facility-unit__reservation-box-btn ${!unitPriceCalculation?.simpleReservationFormCalculatedInformation?.createReservationButtonURL ? 'facility-unit__reservation-box-btn--disabled' : ''}`}
                                            className="adr-btn-primary-lg"
                                            href={finalUrl}
                                            onClick={() => {
                                                if (!IS_FRAME_SERVICE) {
                                                    triggerViewCart();
                                                    dispatchTrackingEvent(RESERVATION_FORM_BOOK_NOW_CTA_CLICK, {
                                                        unitID: mainUnitID,
                                                    });
                                                }
                                            }}
                                        >
                                            {presentationUnit.simpleReservationFormLabels.reserveTitle}
                                        </a>
                                    )}

                                    {/* TODO: DORADIT PO DIZAJNU */}
                                    {unitPriceCalculation?.simpleReservationFormCalculatedInformation?.unalocatedPersonsWarning && (
                                        <div
                                            className="facility-unit__reservation-box-warning-box d-flex justify-content-center align-items-center"
                                            role="button"
                                            tabIndex={0}
                                            onClick={scrollIntoUnitsShortInfoView}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') scrollIntoUnitsShortInfoView();
                                            }}
                                        >
                                            <div className="facility-unit__reservation-box-warning-box-icon d-flex">
                                                <MdInfoOutline className="reservation-box-warning-box-icon" />
                                            </div>
                                            <p
                                                className="facility-unit__reservation-box-warning-box-txt"
                                            >
                                                {unitPriceCalculation.simpleReservationFormCalculatedInformation.unalocatedPersonsWarning}
                                            </p>
                                        </div>
                                    )}
                                    {unitPriceCalculation?.simpleReservationFormCalculatedInformation?.continueWithoutMainUnitText && (
                                        <a
                                            href={unitPriceCalculation.simpleReservationFormCalculatedInformation.continueWithoutMainUnitURL}
                                            className="facility-unit__reservation-box-warning-box d-flex justify-content-center align-items-center"
                                            style={{ textDecoration: 'none', color: 'inherit' }}
                                            target={`${isMobileClient ? '_self' : '_blank'}`}
                                            rel={isMobileClient ? 'noreferrer' : 'noopener noreferrer'}
                                            tabIndex={0}
                                            role="button"
                                        >
                                            <div className="facility-unit__reservation-box-warning-box-icon d-flex">
                                                <MdInfoOutline className="reservation-box-warning-box-icon" />
                                            </div>
                                            <p
                                                className="facility-unit__reservation-box-warning-box-txt"
                                            >
                                                {unitPriceCalculation.simpleReservationFormCalculatedInformation.continueWithoutMainUnitText}
                                            </p>
                                        </a>
                                    )}
                                    {unitPriceCalculation?.simpleReservationFormCalculatedInformation?.freeCancellationTitle && (
                                        <div className="facility-unit__reservation-free-cancel">
                                            <span className="reservation-free-cancel__title">{unitPriceCalculation.simpleReservationFormCalculatedInformation.freeCancellationTitle}</span>
                                            <span className="reservation-free-cancel__txt">{unitPriceCalculation.simpleReservationFormCalculatedInformation.freeCancellationText}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className="facility-unit__login-box-wrap d-none d-lg-block">
                                {!currentUser?.id && presentationUnit.loginRegistrationSection && (
                                    <LoginForMoreBenefits
                                        staticData={presentationUnit.loginRegistrationSection}
                                        trackingEvents={{
                                            onOpenLoginModal: UNIT_PRESENTATION_SIGN_IN_CLICK,
                                        }}
                                        isLoginModalOpenOutsideControl={isLoginModalOpen}
                                        onModalClose={() => setIsLoginModalOpen(false)}
                                    />
                                )}
                            </div>
                        </div>
                    </article>
                    <ReservationMobileModal
                        isModalOpen={isOpenReservationMobileModal}
                        modalHandler={closeReservationMobileModal}
                        sharedData={sharedUnitData}
                        guestSelectHandler={openGuestSelectModal}
                        datesEditHandler={scrollIntoDatePickerView}
                        unitPriceCalculationUnitsById={unitPriceCalculationUnitsById}
                        unitPriceCalculation={unitPriceCalculation}
                        handleRemoveUnit={removeUnitFromSelectedUnitsInReservationarameters}
                        isLoadingUnitPriceCalculation={isLoadingUnitPriceCalculation}
                        handleClickStartReservation={handleClickStartReservation}
                        reservationButtonTextCTA={reservationButtonTextCTA}
                        handleTrackerReservationTextCTA={handleTrackerReservationTextCTA}
                        selectedUnitsAlternative={selectedUnitsAlternative}
                        hasSelectedUnitsFromUnitPriceCalculation={hasSelectedUnitsFromUnitPriceCalculation}
                        handleRemoveSelectedDatesRange={handleDatesChange}
                        isMoreThanOnePresentationUnit={presentationUnit?.units?.unitsShortInfo?.length > 1 || false}
                        dates={{
                            fromText: presentationUnit?.simpleReservationFormLabels?.arrivalTitle,
                            fromValue: startDate ? startDateFormatted : null,
                            toText: presentationUnit?.simpleReservationFormLabels?.departureTitle,
                            toValue: endDate ? endDateFormatted : null,
                        }}
                        additionalText={{
                            showDetailedCalculation: presentationUnit.simpleReservationFormLabels.showDetailedCalculation,
                            howIsThisPriceCalculated: presentationUnit.simpleReservationFormLabels.howIsThisPriceCalculated,
                            detailedCalculation: presentationUnit.simpleReservationFormLabels.detailedCalculation,
                        }}
                        isLoadingReservationPriceCalculationDetails={isLoadingReservationPriceCalculationDetails}
                        reservationPriceCalculationDetails={reservationPriceCalculationDetails}
                    />
                    <GuestSelectModal
                        isModalOpen={isOpenGuestSelectModal}
                        modalHandler={closeGuestSelectModal}
                        guestSetHandler={setModalGuests}
                        modalGuests={modalGuests}
                        unitData={guestsModalUnitData}
                        sharedData={sharedUnitData}
                        confirmHandler={confirmGuestSelectModal}
                        unitPriceCalculation={unitPriceCalculation}
                    />
                    {(unitMainGallery || isLoadingUnitMainGallery) && (
                        <FacilityGallery
                            isModalOpen={isOpenUnitMainGallery}
                            handleClose={() => runAfterNextPaint(() => setIsOpenUnitMainGallery(false))}
                            data={unitMainGallery}
                            isLoading={isLoadingUnitMainGallery}
                        />
                    )}
                    {(presentationUnit.reviews && (facilityUnitReviews || isLoadingReviews)) && (
                        <FacilityReviews
                            modalHandler={handleReviewsModal}
                            isModalOpen={isOpenModalReviews}
                            data={facilityUnitReviews}
                            isLoading={isLoadingReviews}
                            ssrParameters={ssrParameters || null}
                        />
                    )}
                    {(presentationUnitAmenities || isLoadingPresentationUnitAmenities) && (
                        <FacilityDetails
                            modalHandler={handleUnitAmenitiesModal}
                            isModalOpen={isOpenModalUnitAmenities}
                            data={presentationUnitAmenities}
                            isLoading={isLoadingPresentationUnitAmenities}
                            ssrParameters={ssrParameters || null}
                        />
                    )}
                    {(unitMap || isLoadingUnitMap) && (
                        <FacilityMap
                            modalHandler={handleUnitMapModal}
                            isModalOpen={isOpenModalUnitMap}
                            data={unitMap}
                            isLoading={isLoadingUnitMap}
                            ssrParameters={ssrParameters || null}
                        />
                    )}
                    <PaymentDetails
                        modalHandler={handlePaymentDetailsModal}
                        isModalOpen={isOpenModalReservationPriceCalculationDetails}
                        data={reservationPriceCalculationDetails}
                        isLoading={isLoadingReservationPriceCalculationDetails}
                    />
                    {/* TODO: Otvorit ticket za refactor ovog modala, (Loader triba bit unutar modala, sada je na kartici), također galerija unutar modala se mora svaki put iznova fetch-at zbog logike #58747, usput prominut i naming da se zna da je modal. */}
                    <UnitDetails
                        modalHandler={handleSeeMoreAboutUnitModal}
                        isModalOpen={isOpenSeeMoreAboutUnitModal}
                        data={moreAboutUnit[currentRequestedUnitID]}
                        isLoading={isLoadingMoreAboutUnit}
                        priceData={unitPriceCalculationUnitsById[currentRequestedUnitID]}
                        handleConfirm={handleSelectAnotherUnit}
                        handleRemoveUnit={removeUnitFromSelectedUnitsInReservationarameters}
                        sharedData={sharedUnitData}
                        isMobileClient={isMobileClient}
                        dateRangePickerData={{
                            focusedMonth: initialFocusedMonth,
                            startDate,
                            endDate,
                            minDate: parseISO(minVisibleDateOnDateRangePicker),
                            maxDate: parseISO(maxVisibleDateOnDateRangePicker),
                            orientation,
                            onMonthChange: handleCalendarCurrentMonth,
                            ssrParameters,
                            parameterPersons: selectedPersonNumber,
                            parameterNumberOfMonths: numberOfMonthsForFetchBeforeAndAfterFocusedMonth,
                            parameterFromMonth: focusedMonthDateForPriceAvailabilityCalendarRef.current || reservationParameters.intervalFrom || null,
                            parameterIntervalFrom: reservationParameters.intervalFrom || 0,
                            parameterIntervalTo: reservationParameters.intervalTo || 0,
                            handlerGetMonthAndYearFromISODate: getMonthAndYearFromISODate,
                            handlerCompareMonthYearObjects: compareMonthYearObjects,
                            equalMonthAndYearIdentifier,
                        }}
                        priceListAndAvailabilityInfoView={renderPriceListAndAvailabilityInfoView}
                        priceListAndAvailabilityRainyDayRefundView={renderPriceListAndAvailabilityRainyDayRefundView}
                        priceListAndAvailabilityIncludedInPriceView={renderPriceListAndAvailabilityIncludedInPriceView}
                        priceListAndAvailabilityShortStayExtraChargeView={renderPriceListAndAvailabilityShortStayExtraChargeView}
                        priceListAndAvailabilityTouristTaxInfoView={renderPriceListAndAvailabilityTouristTaxInfoView}
                    />
                </section>
            )}
        </>
    );
}

// Koristi se i u express-u za SSR ovoga page-a, zbog toga attach-amo kao static metode na komponentu (attach-amo na kraju
// zbog toga da budemo sigurni da je glavna funkcija(komponenta) definirana u trenutku attach-a).

FacilityUnitContent.defaultVisibleUnitCards = 4;

FacilityUnitContent.getInitialVisibleUnitCardIds = (unitPriceCalculation, initialVisibleUnitCards) => {
    if (!unitPriceCalculation?.units?.length) return [];
    return unitPriceCalculation.units.slice(0, initialVisibleUnitCards).map((item) => item.unitId);
};

export default FacilityUnitContent;
